import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import NoData from "../../Info/NoData";
import {
  processColumnWidth,
  processRowClass,
  processCellClass,
  calculateLeftValue,
  isColumnFixed,
  useColumnWidths,
  useFixedColumns,
  useTableTotals,
  useTablePagination,
  useTableSort,
} from "./tableUtils";
import { formatNumber, keyToTitle } from "../../../utils";
import { compareValues } from "./tableUtils/typeDetection";
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_SELECT_LIST,
  getPaginationRange,
} from "./TableSettings";
import selectOpionGenerator from "./tableUtils/selectOpionGenerator";
import Button from "../../Buttons/Button";
import CustomSelect from "../../FormFields/Custom/CustomSelect";
import DrawerAdv from "../../Modal/DrawerAdv";
import TableDrawerData from "./TableDrawerData";
import DropdownMenuNew from "../../Buttons/DropdownMenuNew";
import Divider from "../../Common/Divider";
import DropdownMenu from "../../Buttons/DropdownMenu";
import Modal from "../../Modal/Modal";
import TableDownloadModalData from "./TableDownloadModalData";
import FormFieldSet from "../../FormFields/FormFieldSet";
import CustomSearch from "../../FormFields/Custom/CustomSearch";
import { EmployeeStatus, Gender } from "../../../assets/data/enumData";
import SmartTooltip from "../../Info/SmartTooltip";

const TableAdv = ({
  color = "primary",
  columns = [],
  rows = [],
  showPages = true,

  //defaultview and onviewchange props belong to list and grid view toggle
  defaultView,
  onViewChange,
  toolbarLeft = {
    add: [],
  },
  toolbarRight = {
    search: true,
    filter: true,
    download: false,
    viewToggle: false,
    fullScreen: false,
    add: [],
    order: [], //["search","add[0]", "viewToggle","add[1]", "download", "filter","fullScreen"]
  },
  showSlNo = false,
  showCheckbox = false,
  onSelectionChange,
  singleSelection = false,
  disableSelection = false,
  disableRowSelection = null, // (row) => boolean — disable checkbox per row
  defaultSelectedRows = [],
  totalSelectedCount = null,
  fixedCol = 0,
  totalText = "Total",
  totalRowStyle = "",
  totalCellStyle = "bg-info-900 text-white font-semibold",
  /////////////////////////Pagination
  page = DEFAULT_PAGE,
  limit = DEFAULT_ROWS_PER_PAGE,
  selectList = DEFAULT_SELECT_LIST,
  //for server pagintion
  serverPagination = false,
  totalCount = null,
  onPaginationChange = () => {},
  onLimitChange = () => {},
  /////////////////////////Pagination end
  tableIdInner = Math.random().toString(36).substring(2, 9),
  //////////////////////////Filter Setting
  tableId = null,
  serverFiltering = false,
  onFilterChange = null,
  //////////////////////////Filter SettingEnd
  //////////////////////////Accordian - NEW
  accordion = null,
  expandedRows = {}, // Changed from array to object: { rowId: 'accordionType' }
  onRowToggle = null,
  //////////////////////////Accordian End - NEW
  //////////////////////////Counm Sort
  sortColunm = false,
  //////////////////////////Counm Sort End
  ///////////////////tool bar
  hideToolBar = false,
  serverOnSearch,
  serverSearchValue,
  ///////////////////tool bar end
  gridViewData = null,
  enableKeyboardNavigation = false,
  noImage,
  noDataDescription,
}) => {
  const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);
  const [localSearchValue, setLocalSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(localSearchValue, 500);
  const [filteredData, setFilteredData] = useState([]);
  const [totalItems, setTotalItems] = useState(rows.length);
  const [userConfigs, setUserConfigs] = useState([]);
  const [activeCell, setActiveCell] = useState(null);
  const [activeConfigName, setActiveConfigName] = useState("System Generated");

  const [toggleInput, setToggleInput] = useState(() => defaultView === "grid");
  const viewMode = toggleInput ? "grid" : "list";

  const tableRef = useRef(null);
  const activeCellRef = useRef(null);
  const selectAllRef = useRef(null);

  // This state is fro handling grid-view data
  const [selected, setSelected] = useState([]);
  const handleSelectionChange = (selected) => {
    setSelected(selected);
  };

  const isRowDisabled = useCallback(
    (row) => {
      if (!disableRowSelection) return false;
      if (typeof disableRowSelection === "function")
        return disableRowSelection(row);
      // If it's a string, treat it as a key on the row object
      if (typeof disableRowSelection === "string")
        return !!row[disableRowSelection];
      // If it's a plain boolean
      return !!disableRowSelection;
    },
    [disableRowSelection],
  );

  const { columnWidths, checkboxWidth, slNoWidth } = useColumnWidths(
    tableRef,
    showCheckbox,
    showSlNo,
  );
  const { fixedColumnStyles, stickyCell, fixedCellBg, headerFixedCellBg } =
    useFixedColumns(fixedCol, color);

  // Use the pagination hook
  const {
    currentPage,
    rowsPerPage,
    startIndex,
    endIndex,
    paginatedData,
    totalPages,
    handlePageChange,
    handleRowsPerPageChange,
  } = useTablePagination({
    serverPagination,
    page,
    limit,
    rows,
    totalCount,
    onPaginationChange,
    onLimitChange,
  });

  // Update selectedRows when defaultSelectedRows changes
  // React.useEffect(() => {
  //   if (defaultSelectedRows.length > 0) {
  //     setSelectedRows(defaultSelectedRows);
  //   }
  // }, [defaultSelectedRows]);
  useEffect(() => {
    const arraysAreDifferent =
      selectedRows.length !== defaultSelectedRows.length ||
      selectedRows.some((id, index) => id !== defaultSelectedRows[index]);

    if (arraysAreDifferent) {
      setSelectedRows(defaultSelectedRows);
    }
  }, [defaultSelectedRows]);

  const style = {
    row: "",
    cell: "px-4 py-2",
    //Header
    headerRow: `bg-${color}-50 border-b border-gray-200`,
    headerCell:
      "overflow-hidden whitespace-nowrap text-left font-normal text-sm ",
    //Body
    bodyRow: "border-b border-gray-200 hover:shadow-md hover:z-30 relative",
    bodyCell: "text-sm",
    //Footer
    footerRow: "",
    footerCell: "",
    // Accordion - NEW
    accordionRow: "bg-gray-50",
    accordionCell: "p-0",
    // Fixed column styles from useFixedColumns hook
    ...fixedColumnStyles,
    // Add explicit sticky header style
    stickyHeader: "sticky top-0 z-40",
  };

  // Determine which rows are displayed based on pagination (will be replaced by filtered version)
  const displayedRows = useMemo(() => {
    if (serverPagination) {
      // For server pagination, all rows provided are already the displayed rows
      return rows;
    } else {
      // For client-side pagination, calculate the displayed rows
      return paginatedData;
    }
  }, [rows, paginatedData, serverPagination]);

  ///////////////Store data in local store on load the component
  const loginUserId = 1;
  const handleFilterUpdate = useCallback(() => {
    setConfigUpdateTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (typeof onViewChange === "function") onViewChange(viewMode);
  }, [viewMode, onViewChange]);

  const handleToggleChange = (payload) => {
    // FormFieldSet might send boolean, or event, or "grid"/"list". Normalize defensively.
    if (typeof payload === "boolean") {
      setToggleInput(payload);
      return;
    }

    if (payload == null) {
      // null/undefined — toggle boolean
      setToggleInput((s) => !s);
      return;
    }

    if (typeof payload === "string") {
      // maybe it sends "grid" or "list"
      setToggleInput(payload === "grid");
      return;
    }

    // if it's an event-like object with target.checked
    if (
      payload &&
      payload.target &&
      typeof payload.target.checked === "boolean"
    ) {
      setToggleInput(Boolean(payload.target.checked));
      return;
    }

    // fallback: toggle
    setToggleInput((s) => !s);
  };

  useEffect(() => {
    if (tableId) {
      const visibleColumns = columns
        .filter((col) => !col.hide)
        .map((col) => ({
          key: col.key,
          label: col.label,
        }));
      // Check if default config already exists
      const existingConfigStr = localStorage.getItem(
        `tableConfig_${tableId}_${loginUserId}`,
      );

      if (existingConfigStr) {
        // If config exists, parse it
        try {
          let existingConfig = JSON.parse(existingConfigStr);
          let existingConfigs = [];
          // Convert to array format if it's a single object
          if (!Array.isArray(existingConfig)) {
            existingConfigs = [existingConfig];
          } else {
            existingConfigs = existingConfig;
          }
          // Find default config or first config
          const defaultConfig =
            existingConfigs.find((config) => config.isDefault) ||
            existingConfigs[0];
          // Check if columns have changed by comparing arrays
          const columnsChanged = !areColumnsEqual(
            defaultConfig.columns,
            visibleColumns,
          );

          if (columnsChanged) {
            // Update only the columns and add updatedOn timestamp for the default config
            const updatedConfigs = existingConfigs.map((config) => {
              if (config.isDefault) {
                return {
                  ...config,
                  columns: visibleColumns,
                  updatedOn: new Date().toISOString(),
                };
              }
              return config;
            });

            localStorage.setItem(
              `tableConfig_${tableId}_${loginUserId}`,
              JSON.stringify(updatedConfigs),
            );
          }
        } catch (error) {
          console.error("Error parsing existing table config:", error);
        }
      } else {
        // Create new configuration object if none exists
        const tableConfig = [
          {
            id: Date.now(), // Generate a unique timestamp ID
            configName: "defaultConfig",
            columns: visibleColumns,
            isActive: true,
            isDefault: true,
            createdDate: new Date().toISOString(),
          },
        ];

        localStorage.setItem(
          `tableConfig_${tableId}_${loginUserId}`,
          JSON.stringify(tableConfig),
        );
      }
    }
  }, [tableId, columns, loginUserId]);
  // Helper function to compare column arrays
  const areColumnsEqual = (cols1, cols2) => {
    if (!cols1 || !cols2 || cols1.length !== cols2.length) return false;
    // Sort both arrays by key to ensure consistent comparison
    const sortedCols1 = [...cols1].sort((a, b) => a.key.localeCompare(b.key));
    const sortedCols2 = [...cols2].sort((a, b) => a.key.localeCompare(b.key));

    return sortedCols1.every(
      (col, index) =>
        col.key === sortedCols2[index].key &&
        col.label === sortedCols2[index].label,
    );
  };
  ///////////////Store data in local store on load the component

  ///////////////Retrieve data from local store where isActive true on load the component
  // Add a state to track configuration changes

  const [configUpdateTrigger, setConfigUpdateTrigger] = useState(0);
  // Function to refresh the active configuration
  const refreshActiveConfig = useCallback(() => {
    setConfigUpdateTrigger((prev) => prev + 1);
  }, []);
  // Add event listener for storage changes and custom events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (tableId && e.key === `tableConfig_${tableId}_${loginUserId}`) {
        refreshActiveConfig();
      }
    };

    const handleCustomEvent = (e) => {
      if (
        tableId &&
        e.detail.tableId === tableId &&
        e.detail.userId === loginUserId
      ) {
        refreshActiveConfig();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tableConfigUpdated", handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tableConfigUpdated", handleCustomEvent);
    };
  }, [tableId, loginUserId, refreshActiveConfig]);

  const activeTableConfig = useMemo(() => {
    if (tableId) {
      const storageKey = `tableConfig_${tableId}_${loginUserId}`;
      const rawData = localStorage.getItem(storageKey);

      if (!rawData) {
        return null;
      }

      try {
        const tableConfig = JSON.parse(rawData);
        // First check if it's a proper array
        if (Array.isArray(tableConfig)) {
          const active = tableConfig.find(
            (config) => config && config.isActive === true,
          );
          if (active) return active;
        }
        // Handle object with numeric keys (looks like an array but isn't)
        if (tableConfig && typeof tableConfig === "object") {
          // Check if it's an object with numeric keys
          for (const key in tableConfig) {
            if (tableConfig[key] && tableConfig[key].isActive === true) {
              return tableConfig[key];
            }
          }
        }
        // Handle single object format
        if (tableConfig && tableConfig.isActive) {
          return tableConfig;
        }

        return null;
      } catch (error) {
        console.error("Error parsing tableConfig:", error);
        return null;
      }
    }
    return null;
  }, [tableId, loginUserId, configUpdateTrigger]); // Add configUpdateTrigger to dependencies
  ///////////////Retrieve data from local store where isActive true on load the component
  useEffect(() => {
    // CLEAN FILTER CONDITIONS BEFORE SAVING
    const cleanAll = (activeTableConfig?.allFilterConditions || []).filter(
      (c) => c.filterColunm && c.operator,
    );

    const cleanAny = (activeTableConfig?.anyFilterConditions || []).filter(
      (c) => c.filterColunm && c.operator,
    );
    if (serverFiltering && onFilterChange && activeTableConfig) {
      const filterConfig = {
        allFilterConditions: cleanAll || [],
        anyFilterConditions: cleanAny || [],
        columns: activeTableConfig.columns || [],
        configName: activeTableConfig.configName,
        configId: activeTableConfig.id,
      };

      onFilterChange(filterConfig);
    }
  }, [serverFiltering, onFilterChange, activeTableConfig, configUpdateTrigger]);

  /////////////////////////////////////////Drawer Setup
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  ///////////////////////////////download button modal setup
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  ///////////////
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  ///////////////

  useEffect(() => {
    if (tableId) {
      const loadUserConfigs = () => {
        const existingConfigStr = localStorage.getItem(
          `tableConfig_${tableId}_${loginUserId}`,
        );

        if (existingConfigStr) {
          try {
            const parsedConfig = JSON.parse(existingConfigStr);
            const configs = Array.isArray(parsedConfig)
              ? parsedConfig
              : [parsedConfig];

            // Filter out system config
            const userOnlyConfigs = configs.filter(
              (config) => !config.isSystem,
            );
            setUserConfigs(userOnlyConfigs);

            // Find active config
            const activeConfig = configs.find((config) => config.isActive);
            if (activeConfig) {
              setActiveConfigName(
                activeConfig.isSystem
                  ? "System Generated"
                  : keyToTitle(activeConfig.configName),
              );
            } else {
              setActiveConfigName("System Generated");
            }
          } catch (error) {
            console.error("Error parsing table config:", error);
            setUserConfigs([]);
            setActiveConfigName("System Generated");
          }
        } else {
          setUserConfigs([]);
          setActiveConfigName("System Generated");
        }
      };

      loadUserConfigs();

      // Listen for config updates
      const handleConfigUpdate = (e) => {
        if (
          tableId &&
          e.detail.tableId === tableId &&
          e.detail.userId === loginUserId
        ) {
          loadUserConfigs();
        }
      };

      window.addEventListener("tableConfigUpdated", handleConfigUpdate);

      return () => {
        window.removeEventListener("tableConfigUpdated", handleConfigUpdate);
      };
    }
  }, [tableId, loginUserId, configUpdateTrigger]);

  const applyFilterConditions = useCallback(
    (rowsToFilter) => {
      if (
        !tableId ||
        !activeTableConfig ||
        !activeTableConfig.allFilterConditions ||
        activeTableConfig.allFilterConditions.length === 0 ||
        serverFiltering
      ) {
        return rowsToFilter;
      }

      const filterConditions = activeTableConfig.allFilterConditions;
      // Group filters by column
      const filtersByColumn = filterConditions.reduce((acc, condition) => {
        const colKey = condition.filterColunm;
        if (!acc[colKey]) acc[colKey] = [];
        acc[colKey].push(condition);
        return acc;
      }, {});
      // ---------------- Helpers ----------------
      const flattenObjectValue = (value) => {
        if (!value || typeof value !== "object") return String(value ?? "");
        const extractValues = (obj) =>
          Object.values(obj)
            .map((v) =>
              typeof v === "object" && v !== null
                ? extractValues(v)
                : String(v ?? ""),
            )
            .filter(Boolean);
        return extractValues(value).join(" ");
      };

      const getCellValue = (row, column) => {
        if (column.valueGetter) return column.valueGetter(row);
        const rawValue = row[column.key];

        if (typeof rawValue === "object") {
          return flattenObjectValue(rawValue);
        }

        return rawValue;
      };

      return rowsToFilter.filter((row) => {
        return Object.entries(filtersByColumn).every(
          ([columnKey, conditions]) => {
            return conditions.some((condition) => {
              if (!condition.filterColunm || !condition.operator) return true;

              const { operator, filterValue, dataType = "string" } = condition;

              const column = columns.find(
                (col) => (col.labelKey || col.key) === columnKey,
              );
              if (!column) return true;

              const cellValue = getCellValue(row, column);

              if (
                cellValue === undefined ||
                cellValue === null ||
                cellValue === ""
              ) {
                return operator === "isEmpty" || operator === "notEqual";
              }

              try {
                return compareValues(
                  cellValue,
                  filterValue,
                  operator,
                  dataType,
                );
              } catch (error) {
                console.error("Filter comparison error:", error);
                return true;
              }
            });
          },
        );
      });
    },
    [tableId, activeTableConfig, columns, serverFiltering],
  );

  const applyOrFilterConditions = useCallback(
    (rowsToFilter) => {
      if (
        serverFiltering ||
        !tableId ||
        !activeTableConfig ||
        !activeTableConfig.anyFilterConditions ||
        activeTableConfig.anyFilterConditions.length === 0
      ) {
        return rowsToFilter;
      }
      const filterConditions = activeTableConfig.anyFilterConditions;
      const filtersByColumn = filterConditions.reduce((acc, condition) => {
        const colKey = condition.filterColunm;
        if (!acc[colKey]) acc[colKey] = [];
        acc[colKey].push(condition);
        return acc;
      }, {});
      // ---------------- Helpers ----------------
      const flattenObjectValue = (value) => {
        if (!value || typeof value !== "object") return String(value ?? "");
        const extractValues = (obj) =>
          Object.values(obj)
            .map((v) =>
              typeof v === "object" && v !== null
                ? extractValues(v)
                : String(v ?? ""),
            )
            .filter(Boolean);
        return extractValues(value).join(" ");
      };

      const getCellValue = (row, column) => {
        if (column.valueGetter) return column.valueGetter(row);
        const rawValue = row[column.key];

        if (typeof rawValue === "object") {
          return flattenObjectValue(rawValue);
        }

        return rawValue;
      };

      return rowsToFilter.filter((row) => {
        return Object.entries(filtersByColumn).some(
          ([columnKey, conditions]) => {
            return conditions.every((condition) => {
              if (!condition.filterColunm || !condition.operator) return true;

              const { operator, filterValue, dataType = "string" } = condition;

              const column = columns.find(
                (col) => (col.labelKey || col.key) === columnKey,
              );
              if (!column) return true;

              const cellValue = getCellValue(row, column);

              if (
                cellValue === undefined ||
                cellValue === null ||
                cellValue === ""
              ) {
                return operator === "isEmpty" || operator === "notEqual";
              }

              try {
                return compareValues(
                  cellValue,
                  filterValue,
                  operator,
                  dataType,
                );
              } catch (error) {
                console.error("Filter comparison error:", error);
                return true;
              }
            });
          },
        );
      });
    },
    [tableId, activeTableConfig, columns, serverFiltering],
  );

  const isValidCondition = (condition) => {
    return (
      condition &&
      condition.filterColunm &&
      condition.filterColunm.trim() !== "" &&
      condition.operator &&
      condition.operator.trim() !== ""
    );
  };
  // Apply filters to rows before pagination
  const filteredRows = useMemo(() => {
    if (serverFiltering) {
      return rows;
    }
    let filtered = rows;

    const allConditions = (activeTableConfig?.allFilterConditions || []).filter(
      isValidCondition,
    );
    const anyConditions = (activeTableConfig?.anyFilterConditions || []).filter(
      isValidCondition,
    );

    if (allConditions.length > 0) {
      filtered = applyFilterConditions(rows);
    }

    if (anyConditions.length > 0) {
      filtered = applyOrFilterConditions(rows);
    }

    return filtered;
  }, [rows, applyFilterConditions, applyOrFilterConditions, serverFiltering]);

  // Update pagination to use filtered rows
  useEffect(() => {
    if (!serverPagination && filteredRows) {
      setTotalItems(filteredRows.length);
    }
  }, [filteredRows, serverPagination]);

  // Use the table sort utility hook
  const { sortedRows, handleSort, sortConfig } = useTableSort(
    filteredRows,
    columns,
  );

  // const searchFilteredRows = useMemo(() => {
  //   if (!localSearchValue.trim() || serverPagination) {
  //     return sortedRows;
  //   }

  //   const searchTerm = localSearchValue.trim().toLowerCase();

  //   return sortedRows.filter((row) =>
  //     Object.entries(row).some(([key, value]) => {
  //       if (value === null || value === undefined) return false;

  //       let textValue = "";

  //       // Map numeric codes to labels dynamically
  //       if (key.toLowerCase().includes("gender")) {
  //         const found = Gender.find((g) => g.value === Number(value));
  //         textValue = found ? found.label : value;
  //       } else if (key.toLowerCase().includes("status")) {
  //         const found = EmployeeStatus.find((s) => s.value === Number(value));
  //         textValue = found ? found.label : value;
  //       }
  //       // Handle nested objects (e.g., { fullName: { first: "John" } })
  //       else if (typeof value === "object") {
  //         textValue = Object.values(value).join(" ");
  //       } else {
  //         textValue = String(value);
  //       }

  //       return textValue.toLowerCase().includes(searchTerm);
  //     }),
  //   );
  // }, [sortedRows, localSearchValue, serverPagination]);

  const searchFilteredRows = useMemo(() => {
    // mahee was updated 2302026
    if (!localSearchValue?.trim() || serverPagination) {
      return sortedRows;
    }
    const searchTerm = localSearchValue.trim().toLowerCase();
    const normalizeValue = (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      if (typeof value === "number" || typeof value === "boolean")
        return String(value);
      if (Array.isArray(value)) {
        return value.map(normalizeValue).join(" ");
      }
      if (typeof value === "object") {
        return Object.values(value).map(normalizeValue).join(" ");
      }
      return "";
    };
    return sortedRows.filter((row) =>
      Object.entries(row).some(([key, value]) => {
        if (value == null) return false;
        let finalValue = value;
        if (key.toLowerCase().includes("gender")) {
          const found = Gender.find((g) => g.value === Number(value));
          finalValue = found?.label ?? value;
        } else if (key.toLowerCase().includes("status")) {
          const found = EmployeeStatus.find((s) => s.value === Number(value));
          finalValue = found?.label ?? value;
        }
        const searchableText = normalizeValue(finalValue).toLowerCase();
        return searchableText.includes(searchTerm);
      }),
    );
  }, [sortedRows, localSearchValue, serverPagination]);

  const displayedRowsWithFilters = useMemo(() => {
    if (serverPagination) {
      return searchFilteredRows;
    } else {
      const start = (currentPage - 1) * rowsPerPage;
      const end = start + rowsPerPage;
      return searchFilteredRows.slice(start, end);
    }
  }, [searchFilteredRows, currentPage, rowsPerPage, serverPagination]);
  
  useEffect(() => {
    if (!selectAllRef.current) return;

    const selectableRows = displayedRowsWithFilters.filter(
      (row) => !isRowDisabled(row),
    );
    const selectableIds = selectableRows.map(
      (row, idx) => row.id || displayedRowsWithFilters.indexOf(row),
    );

    const selectedCount = selectableIds.filter((id) =>
      selectedRows.includes(id),
    ).length;

    const allSelected =
      selectedCount === selectableIds.length && selectableIds.length > 0;
    const someSelected =
      selectedCount > 0 && selectedCount < selectableIds.length;

    selectAllRef.current.checked = allSelected;
    selectAllRef.current.indeterminate = someSelected; // 👈 this is the key part
  }, [selectedRows, displayedRowsWithFilters, isRowDisabled]);

  const { totals, firstTotalIndex, lastTotalIndex, totalLabelColspan } =
    useTableTotals(rows, columns, showCheckbox, showSlNo, displayedRows);

  useEffect(() => {
    if (!serverPagination && localSearchValue.trim()) {
      handlePageChange(1);
    }
  }, [localSearchValue]);

  useEffect(() => {
    if (!enableKeyboardNavigation || viewMode !== "list") return;

    const handleKeyDown = (e) => {
      if (!activeCell) return;

      const visibleColumns = activeTableConfig?.columns
        ? activeTableConfig.columns
            .map((configCol) =>
              columns.find((column) => column.key === configCol.key),
            )
            .filter(Boolean)
        : columns.filter((column) => !column.hide);

      const totalColumns = visibleColumns.length;
      const totalRows = displayedRowsWithFilters.length;

      let newRowIndex = activeCell.rowIndex;
      let newColIndex = activeCell.colIndex;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          newRowIndex = Math.max(0, activeCell.rowIndex - 1);
          break;
        case "ArrowDown":
          e.preventDefault();
          newRowIndex = Math.min(totalRows - 1, activeCell.rowIndex + 1);
          break;
        case "ArrowLeft":
          e.preventDefault();
          newColIndex = Math.max(0, activeCell.colIndex - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          newColIndex = Math.min(totalColumns - 1, activeCell.colIndex + 1);
          break;
        default:
          return;
      }

      setActiveCell({ rowIndex: newRowIndex, colIndex: newColIndex });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeCell,
    enableKeyboardNavigation,
    displayedRowsWithFilters,
    columns,
    activeTableConfig,
    viewMode,
  ]);

  // Scroll active cell into view
  useEffect(() => {
    if (activeCell && activeCellRef.current) {
      activeCellRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeCell]);

  useEffect(() => {
    if (!enableKeyboardNavigation) return;

    const handleClickOutside = (e) => {
      const isModalClick = e.target.closest('[role="dialog"]');

      if (isModalClick) return;

      if (tableRef.current && !tableRef.current.contains(e.target)) {
        setActiveCell(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [enableKeyboardNavigation]);

  const activateConfig = (configId) => {
    const existingConfigStr = localStorage.getItem(
      `tableConfig_${tableId}_${loginUserId}`,
    );

    if (existingConfigStr) {
      try {
        const parsedConfig = JSON.parse(existingConfigStr);
        const configs = Array.isArray(parsedConfig)
          ? parsedConfig
          : [parsedConfig];

        const updatedConfigs = configs.map((config) => ({
          ...config,
          isActive: config.id === configId,
        }));

        localStorage.setItem(
          `tableConfig_${tableId}_${loginUserId}`,
          JSON.stringify(updatedConfigs),
        );

        const event = new CustomEvent("tableConfigUpdated", {
          detail: { tableId, userId: loginUserId },
        });
        window.dispatchEvent(event);

        if (serverFiltering && onFilterChange) {
          const activeConfig = updatedConfigs.find((c) => c.isActive);
          const cleanAll = (activeConfig?.allFilterConditions || []).filter(
            (c) => c.filterColunm && c.operator,
          );

          const cleanAny = (activeConfig?.anyFilterConditions || []).filter(
            (c) => c.filterColunm && c.operator,
          );
          if (activeConfig) {
            onFilterChange({
              allFilterConditions: cleanAll || [],
              anyFilterConditions: cleanAny || [],
              columns: activeConfig.columns || [],
              configName: activeConfig.configName,
              configId: activeConfig.id,
            });
          }
        }

        setConfigUpdateTrigger((prev) => prev + 1);
      } catch (error) {
        console.error("Error activating config:", error);
      }
    }
  };

  const openDrawerWithConfig = (configId) => {
    activateConfig(configId);
    // setIsDrawerOpen(true);
  };

  const toolbarItems = {
    search: toolbarRight?.search && (
      <div key="search" className="md:max-w-40 xl:max-w-max">
        <CustomSearch
          size="sm"
          value={serverPagination ? serverSearchValue : localSearchValue}
          onChange={
            serverPagination
              ? (key, val) => serverOnSearch(key, val)
              : (key, val) => setLocalSearchValue(val)
          }
          keyname="search"
          rounded="rounded"
          closeBtn
        />
      </div>
    ),
    filter: toolbarRight?.filter && (
      <div key="filter">
        {userConfigs.length > 0 ? (
          <div className="relative">
            <DropdownMenu
              MenuSection={
                <div className="flex flex-col p-1">
                  {userConfigs.map((config) => (
                    <Button
                      key={config.id}
                      title={keyToTitle(config.configName)}
                      variant="text"
                      color={config.isActive ? "primary" : "black"}
                      className="!justify-start py-1"
                      onClick={() => openDrawerWithConfig(config.id)}
                    />
                  ))}
                  <Divider className="!my-1" />
                  <Button
                    title="Filter Settings"
                    icon={"settings"}
                    variant="text"
                    color="black"
                    className="!justify-start py-1"
                    onClick={() => {
                      setIsDrawerOpen(true);
                    }}
                  />
                </div>
              }
            >
              <SmartTooltip description={`Active: ${activeConfigName}`} noicon>
                <Button
                  icon="filter_alt"
                  type="icon"
                  variant="text"
                  color="black"
                />
              </SmartTooltip>
            </DropdownMenu>
          </div>
        ) : (
          <Button
            type="icon"
            icon="filter_alt"
            variant="text"
            color="black"
            onClick={() => setIsDrawerOpen(true)}
          />
        )}
      </div>
    ),
    download: toolbarRight?.download && (
      <div key="download">
        <DropdownMenu
          MenuSection={
            <div className="flex flex-col">
              <Button
                title="CSV"
                variant="text"
                color="black"
                className="!justify-start py-1"
                onClick={() => {
                  setDownloadType("CSV");
                  setIsModalOpen(true);
                }}
              />
              <Button
                title="Excel"
                variant="text"
                color="black"
                className="!justify-start py-1"
                onClick={() => {
                  setDownloadType("EXCEL");
                  setIsModalOpen(true);
                }}
              />
            </div>
          }
        >
          <Button icon="download" type="icon" variant="text" color="black" />
        </DropdownMenu>
      </div>
    ),
    viewToggle: toolbarRight?.viewToggle && (
      <div key="viewToggle">
        <FormFieldSet
          value={toggleInput}
          noTitle
          size="sm"
          rounded="sm"
          type="toggleInput"
          onChange={handleToggleChange}
          leftIcon="dashboard_2"
          rightIcon="event_list"
        />
      </div>
    ),
    fullScreen: toolbarRight?.fullScreen && (
      <div key="fullScreen">
        <Button
          icon={!isFullscreenOpen? "fullscreen": "fullscreen_exit"}
          type="icon"
          variant="text"
          color="black"
          onClick={() => setIsFullscreenOpen(!isFullscreenOpen)}
        />
      </div>
    ),
  };
  const defaultOrder = [
    "search",
    "filter",
    "download",
    "viewToggle",
    "add",
    "fullScreen",
  ];
  const order = toolbarRight?.order || defaultOrder;

  const PageNumbers = () => {
    const computedTotalPages =
      serverPagination || serverFiltering
        ? totalPages
        : Math.ceil(searchFilteredRows.length / rowsPerPage);

    const pages = getPaginationRange(currentPage, computedTotalPages);

    if (!pages.length) return null;

    return (
      <div className="flex items-center">
        {pages.map((page, index) => {
          if (page === "...") {
            return (
              <span key={`ellipsis-${index}`} className="px-0! text-gray-400">
                ...
              </span>
            );
          }

          return (
            <Button
              // key={page}
              key={`page-${page}-${index}`}
              size="sm"
              variant={currentPage === page ? "contained" : "text"}
              color={currentPage === page ? "primary" : "black"}
              title={String(page)}
              className="min-w-[20px] !px-0 !py-0"
              onClick={() => {
                handlePageChange(page);
                setLocalSearchValue("");
              }}
              data-dropdown-persist="true"
            />
          );
        })}
      </div>
    );
  };

  const tableBody = (
    <div className="flex-1 w-full flex flex-col gap-0 overflow-hidden">
      {!hideToolBar && (
        <div className="tableTools py-2 px-2 flex justify-between items-center text-sm flex-wrap">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <span>
                {!totalCount && !searchFilteredRows?.length
                  ? startIndex
                  : startIndex + 1}
                -
                {Math.min(
                  endIndex,
                  serverPagination || serverFiltering
                    ? totalCount
                    : searchFilteredRows.length,
                )}
              </span>
              <span className="text-nowrap">
                {" "}
                of{" "}
                {serverPagination || serverFiltering
                  ? totalCount
                  : searchFilteredRows.length}
              </span>

              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  type="icon"
                  variant="text"
                  color="black"
                  icon="chevron_left"
                  onClick={() => {
                    handlePageChange(currentPage - 1);
                    setLocalSearchValue("");
                  }}
                  disabled={currentPage === 1}
                  data-dropdown-persist="true"
                />
                {showPages && <PageNumbers />}

                <Button
                  size="sm"
                  icon="chevron_right"
                  type="icon"
                  variant="text"
                  color="black"
                  onClick={() => {
                    handlePageChange(currentPage + 1);
                    setLocalSearchValue("");
                  }}
                  disabled={
                    serverPagination || serverFiltering
                      ? currentPage >= totalPages
                      : currentPage >=
                        Math.ceil(searchFilteredRows.length / rowsPerPage)
                  }
                  data-dropdown-persist="true"
                />
              </div>
              <div className="w-32 lg:w-40">
                <CustomSelect
                  size="sm"
                  placeholder="Select rows"
                  options={selectOpionGenerator(selectList, rowsPerPage)}
                  selected={String(rowsPerPage)}
                  onChange={handleRowsPerPageChange}
                  data-dropdown-persist="true"
                />
              </div>
              {(selectedRows.length > 0 ||
                (totalSelectedCount !== null && totalSelectedCount > 0)) && (
                <div className="flex gap-2 items-center text-xs">
                  Total Selected rows:{" "}
                  {totalSelectedCount !== null
                    ? totalSelectedCount
                    : selectedRows.length}{" "}
                  <Button
                    type="icon"
                    icon={"close"}
                    size="xs"
                    variant="text"
                    onClick={() => {
                      setSelectedRows([]);
                      if (onSelectionChange) {
                        onSelectionChange([]);
                      }
                    }}
                  />
                </div>
              )}
              <div className="flex items-center gap-1">
                {toolbarLeft?.add &&
                  toolbarLeft.add.map((item, key) => (
                    <div key={`toolbar-add-${key}`}>
                      {item.component ? (
                        item.component
                      ) : (
                        <Button
                          icon={item?.icon}
                          type="icon"
                          variant="text"
                          color="black"
                          onClick={item?.onClick}
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="toolbar-right flex items-center gap-2 mr-1">
            {order.map((key) => {
              if (key.startsWith("add[")) {
                const index = parseInt(key.match(/\[(\d+)\]/)?.[1]);
                const addWrapper = toolbarRight?.add?.[0];
                if (!addWrapper) return null;

                const Comp = addWrapper.component;

                if (React.isValidElement(Comp) && Comp.props?.children) {
                  const childrenArray = React.Children.toArray(
                    Comp.props.children,
                  );
                  const targetChild = childrenArray[index];
                  if (!targetChild) return null;

                  return (
                    <div key={key} className="flex items-center">
                      {targetChild}
                    </div>
                  );
                }

                if (React.isValidElement(Comp) && index === 0) {
                  return (
                    <div key={key} className="flex items-center">
                      {Comp}
                    </div>
                  );
                }

                return null;
              }

              if (key === "add") {
                const addWrapper = toolbarRight?.add?.[0];
                if (!addWrapper) return null;

                const Comp = addWrapper.component;
                if (React.isValidElement(Comp)) {
                  if (Comp.props?.children) {
                    return (
                      <div
                        key="add"
                        className="flex flex-row items-center gap-2"
                      >
                        {React.Children.toArray(Comp.props.children)}
                      </div>
                    );
                  }

                  return (
                    <div key="add" className="flex flex-row items-center gap-2">
                      {Comp}
                    </div>
                  );
                }

                return null;
              }

              if (toolbarItems[key]) {
                return <div key={key}>{toolbarItems[key]}</div>;
              }

              return null;
            })}
          </div>
        </div>
      )}

      {viewMode === "list" && (
        <React.Fragment>
          <div
            className={`tableWrapper flex-1  flex items-start  ${displayedRowsWithFilters.length === 0 ? "overflow-hidden" : "overflow-auto"}`}
          >
            <table className="min-w-full w-fit table-auto" ref={tableRef}>
              <thead className={`${style.stickyHeader}`}>
                <tr className={`${style.headerRow} ${style.row}`}>
                  {showCheckbox && (
                    <th
                      className={`${style.headerCell} ${style.cell} w-12 min-w-12 ${
                        isColumnFixed(-2, fixedCol) || fixedCol > 0
                          ? `${style.stickyCell} ${headerFixedCellBg}`
                          : ""
                      }`}
                      style={
                        isColumnFixed(-2, fixedCol) || fixedCol > 0
                          ? {
                              ...fixedColumnStyles,
                              left: calculateLeftValue(
                                columnWidths,
                                -2,
                                checkboxWidth,
                                slNoWidth,
                                showCheckbox,
                                showSlNo,
                              ),
                              zIndex: 40,
                            }
                          : {}
                      }
                    >
                      {!singleSelection && !disableSelection && (
                        // <input
                        //   type="checkbox"
                        //   className="w-3 h-3"
                        //   checked={
                        //     displayedRowsWithFilters.length > 0 &&
                        //     displayedRowsWithFilters.every((row) =>
                        //       selectedRows.includes(
                        //         row.id || displayedRowsWithFilters.indexOf(row),
                        //       ),
                        //     )
                        //   }
                        //   onChange={() => {
                        //     const visibleRowIds = displayedRowsWithFilters.map(
                        //       (row, idx) => row.id || idx,
                        //     );
                        //     const allVisibleSelected = visibleRowIds.every(
                        //       (id) => selectedRows.includes(id),
                        //     );

                        //     if (allVisibleSelected) {
                        //       setSelectedRows(
                        //         selectedRows.filter(
                        //           (id) => !visibleRowIds.includes(id),
                        //         ),
                        //       );
                        //     } else {
                        //       setSelectedRows([
                        //         ...selectedRows.filter(
                        //           (id) => !visibleRowIds.includes(id),
                        //         ),
                        //         ...visibleRowIds,
                        //       ]);
                        //     }

                        //     if (onSelectionChange) {
                        //       onSelectionChange(
                        //         allVisibleSelected
                        //           ? selectedRows.filter(
                        //               (id) => !visibleRowIds.includes(id),
                        //             )
                        //           : [
                        //               ...selectedRows.filter(
                        //                 (id) => !visibleRowIds.includes(id),
                        //               ),
                        //               ...visibleRowIds,
                        //             ],
                        //       );
                        //     }
                        //   }}
                        // />
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          className="w-3 h-3"
                          // checked={(() => {
                          //   const selectableRows =
                          //     displayedRowsWithFilters.filter(
                          //       (row) => !isRowDisabled(row),
                          //     );
                          //   return (
                          //     selectableRows.length > 0 &&
                          //     selectableRows.every((row) =>
                          //       selectedRows.includes(
                          //         row.id ||
                          //           displayedRowsWithFilters.indexOf(row),
                          //       ),
                          //     )
                          //   );
                          // })()}
                          onChange={() => {
                            const selectableRows =
                              displayedRowsWithFilters.filter(
                                (row) => !isRowDisabled(row),
                              );
                            const selectableIds = selectableRows.map(
                              (row) =>
                                row.id || displayedRowsWithFilters.indexOf(row),
                            );
                            const allSelectableSelected = selectableIds.every(
                              (id) => selectedRows.includes(id),
                            );

                            let newSelected;
                            if (allSelectableSelected) {
                              newSelected = selectedRows.filter(
                                (id) => !selectableIds.includes(id),
                              );
                            } else {
                              newSelected = [
                                ...selectedRows.filter(
                                  (id) => !selectableIds.includes(id),
                                ),
                                ...selectableIds,
                              ];
                            }

                            setSelectedRows(newSelected);
                            if (onSelectionChange)
                              onSelectionChange(newSelected);
                          }}
                        />
                      )}
                    </th>
                  )}
                  {showSlNo && (
                    <th
                      className={`${style.headerCell} ${style.cell} w-16 min-w-[64px] ${
                        isColumnFixed(-1, fixedCol) || fixedCol > 0
                          ? `${style.stickyCell} ${headerFixedCellBg}`
                          : ""
                      }`}
                      style={
                        isColumnFixed(-1, fixedCol) || fixedCol > 0
                          ? {
                              ...fixedColumnStyles,
                              left: calculateLeftValue(
                                columnWidths,
                                -1,
                                checkboxWidth,
                                slNoWidth,
                                showCheckbox,
                                showSlNo,
                              ),
                              zIndex: 40,
                            }
                          : {}
                      }
                    >
                      S.No
                    </th>
                  )}
                  {(activeTableConfig?.columns
                    ? activeTableConfig.columns
                        .map((configCol) =>
                          columns.find(
                            (column) => column.key === configCol.key,
                          ),
                        )
                        .filter(Boolean)
                    : columns.filter((column) => !column.hide)
                  ).map((column, colIndex) => {
                    const { classes, styles: columnStyles } =
                      processColumnWidth(column.width);
                    const isFixed = isColumnFixed(colIndex, fixedCol);
                    const leftPosition = calculateLeftValue(
                      columnWidths,
                      colIndex,
                      checkboxWidth,
                      slNoWidth,
                      showCheckbox,
                      showSlNo,
                    );

                    return (
                      <th
                        key={colIndex}
                        className={`${style.headerCell} ${style.cell} ${classes} ${
                          isFixed
                            ? `${style.stickyCell} ${headerFixedCellBg}`
                            : ""
                        }`}
                        style={{
                          ...columnStyles,
                          ...(isFixed
                            ? {
                                ...fixedColumnStyles,
                                left: `${leftPosition}px`,
                                zIndex: 30,
                              }
                            : {}),
                        }}
                      >
                        <div className="flex justify-between items-center gap-5">
                          <div className="flex items-center gap-2">
                            {column.label}
                            {column.sortfun &&
                              sortConfig.key === column.key && (
                                <span className="material-symbols-outlined !text-xs text-primary-200">
                                  {sortConfig.direction === "asc"
                                    ? "north"
                                    : "south"}
                                </span>
                              )}
                          </div>
                          {column.key !== "" && column.columnMenu !== false && (
                            <div>
                              <DropdownMenuNew
                                MenuSection={
                                  <div className="p-1 flex flex-col gap-1">
                                    {column.dataType !== "array" && (
                                      <>
                                        <Button
                                          color={
                                            sortConfig.key === column.key &&
                                            sortConfig.direction === "asc"
                                              ? "primary"
                                              : "black"
                                          }
                                          title={"Sort by ASC"}
                                          variant="text"
                                          size="sm"
                                          icon="north"
                                          className="w-full text-left justify-start !px-1"
                                          onClick={() => {
                                            if (column.sortfun) {
                                              column.sortfun("asc");
                                            } else {
                                              handleSort(column.key, "asc");
                                            }
                                          }}
                                        />
                                        <Button
                                          color={
                                            sortConfig.key === column.key &&
                                            sortConfig.direction === "desc"
                                              ? "primary"
                                              : "black"
                                          }
                                          title={"Sort by DESC"}
                                          variant="text"
                                          size="sm"
                                          icon="south"
                                          className="w-full text-left justify-start !px-1"
                                          onClick={() => {
                                            if (column.sortfun) {
                                              column.sortfun("desc");
                                            } else {
                                              handleSort(column.key, "desc");
                                            }
                                          }}
                                        />
                                        <Divider className="!my-1" />
                                      </>
                                    )}
                                    <Button
                                      disabled
                                      color="black"
                                      title={"Hide Column"}
                                      variant="text"
                                      size="sm"
                                      icon="visibility_off"
                                      className="w-full text-left justify-start !px-1"
                                    />
                                  </div>
                                }
                              >
                                <Button
                                  color="black"
                                  size="xs"
                                  title={"More"}
                                  icon="more_vert"
                                  type="icon"
                                  variant="text"
                                />
                              </DropdownMenuNew>
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayedRowsWithFilters.length === 0 ? (
                  <tr>
                    <td
                      className="text-center"
                      colSpan={
                        (activeTableConfig?.columns
                          ? activeTableConfig.columns.filter((configCol) =>
                              columns.find(
                                (col) => col.key === configCol.key && !col.hide,
                              ),
                            ).length
                          : columns.filter((col) => !col.hide).length) +
                        (showSlNo ? 1 : 0) +
                        (showCheckbox ? 1 : 0)
                      }
                    >
                      {/* <div className="flex justify-center py-8">
                        <NoData />
                      </div> */}
                    </td>
                  </tr>
                ) : (
                  displayedRowsWithFilters.map((row, rowIndex) => {
                    const rowId = row.id || rowIndex;
                    const isExpanded = expandedRows[rowId];
                    const rowClass = processRowClass(row, columns);

                    const rowDisabled = disableSelection || isRowDisabled(row);

                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          className={`${style.bodyRow} ${style.row} ${rowClass} group`}
                        >
                          {showCheckbox && (
                            <td
                              className={`${style.bodyCell} ${style.cell} w-12 min-w-[48px] ${
                                isColumnFixed(-2, fixedCol) || fixedCol > 0
                                  ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
                                  : ""
                              }`}
                              style={
                                isColumnFixed(-2, fixedCol) || fixedCol > 0
                                  ? {
                                      ...fixedColumnStyles,
                                      left: calculateLeftValue(
                                        columnWidths,
                                        -2,
                                        checkboxWidth,
                                        slNoWidth,
                                        showCheckbox,
                                        showSlNo,
                                      ),
                                      zIndex: 20,
                                    }
                                  : {}
                              }
                            >
                              <input
                                type="checkbox"
                                className={`w-3 h-3 ${rowDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                checked={selectedRows.includes(
                                  row.id || rowIndex,
                                )}
                                disabled={rowDisabled}
                                onChange={(e) => {
                                  const rowId = row.id || rowIndex;
                                  if (singleSelection) {
                                    if (e.target.checked) {
                                      setSelectedRows([rowId]);
                                      if (onSelectionChange)
                                        onSelectionChange([rowId]);
                                    } else {
                                      setSelectedRows([]);
                                      if (onSelectionChange)
                                        onSelectionChange([]);
                                    }
                                  } else {
                                    if (e.target.checked) {
                                      const newSelected = [
                                        ...selectedRows,
                                        rowId,
                                      ];
                                      setSelectedRows(newSelected);
                                      if (onSelectionChange)
                                        onSelectionChange(newSelected);
                                    } else {
                                      const newSelected = selectedRows.filter(
                                        (id) => id !== rowId,
                                      );
                                      setSelectedRows(newSelected);
                                      if (onSelectionChange)
                                        onSelectionChange(newSelected);
                                    }
                                  }
                                }}
                              />
                            </td>
                          )}
                          {showSlNo && (
                            <td
                              className={`${style.bodyCell} ${style.cell} w-16 min-w-[64px] ${
                                isColumnFixed(-1, fixedCol) || fixedCol > 0
                                  ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
                                  : ""
                              }`}
                              style={
                                isColumnFixed(-1, fixedCol) || fixedCol > 0
                                  ? {
                                      ...fixedColumnStyles,
                                      left: calculateLeftValue(
                                        columnWidths,
                                        -1,
                                        checkboxWidth,
                                        slNoWidth,
                                        showCheckbox,
                                        showSlNo,
                                      ),
                                      zIndex: 20,
                                    }
                                  : {}
                              }
                            >
                              {startIndex + rowIndex + 1}
                            </td>
                          )}
                          {(activeTableConfig?.columns
                            ? activeTableConfig.columns
                                .map((configCol) =>
                                  columns.find(
                                    (column) => column.key === configCol.key,
                                  ),
                                )
                                .filter(Boolean)
                            : columns.filter((column) => !column.hide)
                          ).map((column, colIndex) => {
                            const { classes, styles: columnStyles } =
                              processColumnWidth(column.width);
                            const cellClass = processCellClass(row, column);
                            const isFixed = isColumnFixed(colIndex, fixedCol);
                            const leftPosition = calculateLeftValue(
                              columnWidths,
                              colIndex,
                              checkboxWidth,
                              slNoWidth,
                              showCheckbox,
                              showSlNo,
                            );

                            const isActiveCell =
                              enableKeyboardNavigation &&
                              activeCell?.rowIndex === rowIndex &&
                              activeCell?.colIndex === colIndex;

                            return (
                              <td
                                key={colIndex}
                                ref={isActiveCell ? activeCellRef : null}
                                className={`${style.bodyCell} ${style.cell} ${classes} ${cellClass} ${
                                  isFixed
                                    ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
                                    : ""
                                } ${
                                  isActiveCell
                                    ? `border-1 border-${color}-500`
                                    : ""
                                } outline-none`}
                                style={{
                                  ...columnStyles,
                                  ...(isFixed
                                    ? {
                                        ...fixedColumnStyles,
                                        left: `${leftPosition}px`,
                                        zIndex: 20,
                                      }
                                    : {}),
                                }}
                                onClick={() => {
                                  if (enableKeyboardNavigation) {
                                    setActiveCell({ rowIndex, colIndex });
                                  }
                                }}
                                tabIndex={
                                  enableKeyboardNavigation ? 0 : undefined
                                }
                              >
                                {column.render
                                  ? column.render(row)
                                  : row[column.key]}
                              </td>
                            );
                          })}
                        </tr>

                        {isExpanded && accordion && (
                          <tr className={style.accordionRow}>
                            <td
                              colSpan={
                                (activeTableConfig?.columns
                                  ? activeTableConfig.columns.filter(
                                      (configCol) =>
                                        columns.find(
                                          (col) =>
                                            col.key === configCol.key &&
                                            !col.hide,
                                        ),
                                    ).length
                                  : columns.filter((col) => !col.hide).length) +
                                (showSlNo ? 1 : 0) +
                                (showCheckbox ? 1 : 0)
                              }
                              className={style.accordionCell}
                            >
                              <div className="w-full">
                                {typeof accordion === "function"
                                  ? accordion(row, isExpanded)
                                  : React.isValidElement(accordion)
                                    ? React.cloneElement(accordion, {
                                        row,
                                        data: row,
                                        accordionType: isExpanded,
                                      })
                                    : accordion}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
              {firstTotalIndex >= 0 &&
                (activeTableConfig?.columns
                  ? activeTableConfig.columns
                      .map((configCol) =>
                        columns.find((column) => column.key === configCol.key),
                      )
                      .filter(Boolean)
                      .some((column) => column.total)
                  : columns
                      .filter((column) => !column.hide)
                      .some((column) => column.total)) && (
                  <tfoot style={{ position: "relative", zIndex: 2 }}>
                    <tr className={`${totalRowStyle}  bg-gray-100`}>
                      {firstTotalIndex > 0 && (
                        <td
                          colSpan={
                            (activeTableConfig?.columns
                              ? activeTableConfig.columns
                                  .map((configCol) =>
                                    columns.find(
                                      (column) => column.key === configCol.key,
                                    ),
                                  )
                                  .filter(Boolean)
                                  .filter(
                                    (column) =>
                                      columns.indexOf(column) <
                                        firstTotalIndex && !column.hide,
                                  )
                              : columns.filter(
                                  (col) =>
                                    !col.hide &&
                                    columns.indexOf(col) < firstTotalIndex,
                                )
                            ).length +
                            (showCheckbox ? 1 : 0) +
                            (showSlNo ? 1 : 0)
                          }
                          className={`${style.footerCell} ${style.cell} text-right font-semibold`}
                          style={
                            fixedCol > 0
                              ? {
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 10,
                                  backgroundColor: "inherit",
                                }
                              : {}
                          }
                        >
                          {totalText}
                        </td>
                      )}
                      {(activeTableConfig?.columns
                        ? activeTableConfig.columns
                            .map((configCol) =>
                              columns.find(
                                (column) => column.key === configCol.key,
                              ),
                            )
                            .filter(Boolean)
                        : columns.filter((column) => !column.hide)
                      ).map((column, colIndex) => {
                        const originalIndex = columns.findIndex(
                          (col) => col.key === column.key,
                        );

                        if (originalIndex < firstTotalIndex) return null;
                        const dynamicCellClass = column.getCellClass
                          ? column.getCellClass({})
                          : "";

                        const isFooterFixed = colIndex < fixedCol;

                        return (
                          <td
                            key={`footer-${column.key}-${colIndex}`}
                            className={`px-4 py-2 truncate overflow-hidden whitespace-nowrap text-right ${
                              originalIndex <= lastTotalIndex
                                ? totalCellStyle
                                : ""
                            } ${column.className || ""} ${dynamicCellClass}`}
                            style={{
                              ...processColumnWidth(column.width).styles,
                              ...(isFooterFixed
                                ? {
                                    position: "sticky",
                                    left: `${calculateLeftValue(
                                      columnWidths,
                                      colIndex,
                                      checkboxWidth,
                                      slNoWidth,
                                      showCheckbox,
                                      showSlNo,
                                    )}px`,
                                    zIndex: 10,
                                    backgroundColor: "inherit",
                                  }
                                : {
                                    // Non-fixed: no z-index, no position — slides under fixed cols
                                    position: "relative",
                                    zIndex: 0,
                                  }),
                            }}
                          >
                            {column.total
                              ? column.totalRender
                                ? column.totalRender(totals[column.key], totals)
                                : formatNumber(totals[column.key]) || "0.00"
                              : ""}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
            </table>
          </div>
          {displayedRowsWithFilters.length === 0 && (
            <div className="flex justify-center py-8">
              <NoData noImage={noImage} desc={noDataDescription} />
            </div>
          )}
        </React.Fragment>
      )}

      {viewMode === "grid" &&
        gridViewData &&
        React.isValidElement(gridViewData) &&
        React.cloneElement(gridViewData, {})}
    </div>
  );

  return (
    <div className="flex-1 w-full flex flex-col gap-1 overflow-hidden">
      {!isFullscreenOpen && tableBody}

      <DrawerAdv
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        // position="bottom"
        title={"Table Filter"}
        closeOnOutsideClick
        height="h-[90%]"
        width="2xl:w-7xl xl:w-5xl lg:w-3xl md:w-xl"
      >
        <TableDrawerData
          columns={columns}
          rows={rows}
          userId={loginUserId}
          tableId={tableId}
          showSlNo={showSlNo}
          fixedCol={fixedCol}
          onFilterUpdate={handleFilterUpdate}
          serverFiltering={serverFiltering}
          onServerFilterChange={onFilterChange}
          onDrawerClose={() => setIsDrawerOpen(false)}
        />
      </DrawerAdv>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Download ${downloadType} file`}
        width="max-w-4xl"
        closeOnOutsideClick
      >
        <TableDownloadModalData
          downloadType={downloadType}
          columns={columns}
          rows={rows}
          selectedRows={selectedRows}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onClose={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        //title="Table"
        showCloseButton={false}
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        width="max-w-[100vw]"
        wrapperClassName="p-0!"
        panelClassName="!max-h-[100vh] h-[100dvh] rounded-none!"
        bodyClassName="p-0! flex flex-1 overflow-hidden"
        closeOnOutsideClick
        zIndex={48}
      >
        <div className="w-full overflow-hidden flex flex-col flex-1">
          {/* <div className="flex justify-end p-2">
            <Button
              onClick={() => setIsFullscreenOpen(false)}
              type="icon"
              icon={"close"}
              variant="text"
              size="xs-sm"
              color="danger"
            />
          </div> */}
          {tableBody}
        </div>
      </Modal>
    </div>
  );
};

export default TableAdv;

// before disabling the rows
// import React, {
//   useState,
//   useRef,
//   useMemo,
//   useEffect,
//   useCallback,
// } from "react";
// import { useDebounce } from "../../../hooks/useDebounce";
// import NoData from "../../Info/NoData";
// import {
//   processColumnWidth,
//   processRowClass,
//   processCellClass,
//   calculateLeftValue,
//   isColumnFixed,
//   useColumnWidths,
//   useFixedColumns,
//   useTableTotals,
//   useTablePagination,
//   useTableSort,
// } from "./tableUtils";
// import { formatNumber, keyToTitle } from "../../../utils";
// import { compareValues } from "./tableUtils/typeDetection";
// import {
//   DEFAULT_PAGE,
//   DEFAULT_ROWS_PER_PAGE,
//   DEFAULT_SELECT_LIST,
// } from "./TableSettings";
// import selectOpionGenerator from "./tableUtils/selectOpionGenerator";
// import Button from "../../Buttons/Button";
// import CustomSelect from "../../FormFields/Custom/CustomSelect";
// import DrawerAdv from "../../Modal/DrawerAdv";
// import TableDrawerData from "./TableDrawerData";
// import DropdownMenuNew from "../../Buttons/DropdownMenuNew";
// import Divider from "../../Common/Divider";
// import DropdownMenu from "../../Buttons/DropdownMenu";
// import Modal from "../../Modal/Modal";
// import TableDownloadModalData from "./TableDownloadModalData";
// import FormFieldSet from "../../FormFields/FormFieldSet";
// import CustomSearch from "../../FormFields/Custom/CustomSearch";
// import { EmployeeStatus, Gender } from "../../../assets/data/enumData";
// import SmartTooltip from "../../Info/SmartTooltip";

// const TableAdv = ({
//   color = "primary",
//   columns = [],
//   rows = [],
//   showPages = true,

//   //defaultview and onviewchange props belong to list and grid view toggle
//   defaultView,
//   onViewChange,
//   toolbarLeft = {
//     add: [],
//   },
//   toolbarRight = {
//     search: true,
//     filter: true,
//     download: false,
//     viewToggle: false,
//     fullScreen: false,
//     add: [],
//     order: [], //["search","add[0]", "viewToggle","add[1]", "download", "filter","fullScreen"]
//   },
//   showSlNo = false,
//   showCheckbox = false,
//   onSelectionChange,
//   singleSelection = false,
//   disableSelection = false,
//   defaultSelectedRows = [],
//   totalSelectedCount = null,
//   fixedCol = 0,
//   totalText = "Total",
//   totalRowStyle = "",
//   totalCellStyle = "bg-info-900 text-white font-semibold",
//   /////////////////////////Pagination
//   page = DEFAULT_PAGE,
//   limit = DEFAULT_ROWS_PER_PAGE,
//   selectList = DEFAULT_SELECT_LIST,
//   //for server pagintion
//   serverPagination = false,
//   totalCount = null,
//   onPaginationChange = () => {},
//   onLimitChange = () => {},
//   /////////////////////////Pagination end
//   tableIdInner = Math.random().toString(36).substring(2, 9),
//   //////////////////////////Filter Setting
//   tableId = null,
//   serverFiltering = false,
//   onFilterChange = null,
//   //////////////////////////Filter SettingEnd
//   //////////////////////////Accordian - NEW
//   accordion = null,
//   expandedRows = {}, // Changed from array to object: { rowId: 'accordionType' }
//   onRowToggle = null,
//   //////////////////////////Accordian End - NEW
//   //////////////////////////Counm Sort
//   sortColunm = false,
//   //////////////////////////Counm Sort End
//   ///////////////////tool bar
//   hideToolBar = false,
//   serverOnSearch,
//   serverSearchValue,
//   ///////////////////tool bar end
//   gridViewData = null,
//   enableKeyboardNavigation = false,
//   noImage,
//   noDataDescription,
// }) => {
//   const [selectedRows, setSelectedRows] = useState(defaultSelectedRows);
//   const [localSearchValue, setLocalSearchValue] = useState("");
//   const debouncedSearchValue = useDebounce(localSearchValue, 500);
//   const [filteredData, setFilteredData] = useState([]);
//   const [totalItems, setTotalItems] = useState(rows.length);
//   const [userConfigs, setUserConfigs] = useState([]);
//   const [activeCell, setActiveCell] = useState(null);
//   const [activeConfigName, setActiveConfigName] = useState("System Generated");

//   const [toggleInput, setToggleInput] = useState(() => defaultView === "grid");
//   const viewMode = toggleInput ? "grid" : "list";

//   const tableRef = useRef(null);
//   const activeCellRef = useRef(null);

//   // This state is fro handling grid-view data
//   const [selected, setSelected] = useState([]);
//   const handleSelectionChange = (selected) => {
//     setSelected(selected);
//   };

//   const { columnWidths, checkboxWidth, slNoWidth } = useColumnWidths(
//     tableRef,
//     showCheckbox,
//     showSlNo,
//   );
//   const { fixedColumnStyles, stickyCell, fixedCellBg, headerFixedCellBg } =
//     useFixedColumns(fixedCol, color);

//   // Use the pagination hook
//   const {
//     currentPage,
//     rowsPerPage,
//     startIndex,
//     endIndex,
//     paginatedData,
//     totalPages,
//     handlePageChange,
//     handleRowsPerPageChange,
//   } = useTablePagination({
//     serverPagination,
//     page,
//     limit,
//     rows,
//     totalCount,
//     onPaginationChange,
//     onLimitChange,
//   });

//   // Update selectedRows when defaultSelectedRows changes
//   // React.useEffect(() => {
//   //   if (defaultSelectedRows.length > 0) {
//   //     setSelectedRows(defaultSelectedRows);
//   //   }
//   // }, [defaultSelectedRows]);
//   useEffect(() => {
//     const arraysAreDifferent =
//       selectedRows.length !== defaultSelectedRows.length ||
//       selectedRows.some((id, index) => id !== defaultSelectedRows[index]);

//     if (arraysAreDifferent) {
//       setSelectedRows(defaultSelectedRows);
//     }
//   }, [defaultSelectedRows]);

//   const style = {
//     row: "",
//     cell: "px-4 py-2",
//     //Header
//     headerRow: `bg-${color}-50 border-b border-gray-200`,
//     headerCell:
//       "overflow-hidden whitespace-nowrap text-left font-normal text-sm ",
//     //Body
//     bodyRow: "border-b border-gray-200 hover:shadow-md hover:z-30 relative",
//     bodyCell: "text-sm",
//     //Footer
//     footerRow: "",
//     footerCell: "",
//     // Accordion - NEW
//     accordionRow: "bg-gray-50",
//     accordionCell: "p-0",
//     // Fixed column styles from useFixedColumns hook
//     ...fixedColumnStyles,
//     // Add explicit sticky header style
//     stickyHeader: "sticky top-0 z-40",
//   };

//   // Determine which rows are displayed based on pagination (will be replaced by filtered version)
//   const displayedRows = useMemo(() => {
//     if (serverPagination) {
//       // For server pagination, all rows provided are already the displayed rows
//       return rows;
//     } else {
//       // For client-side pagination, calculate the displayed rows
//       return paginatedData;
//     }
//   }, [rows, paginatedData, serverPagination]);

//   ///////////////Store data in local store on load the component
//   const loginUserId = 1;
//   const handleFilterUpdate = useCallback(() => {
//     setConfigUpdateTrigger((prev) => prev + 1);
//   }, []);

//   useEffect(() => {
//     if (typeof onViewChange === "function") onViewChange(viewMode);
//   }, [viewMode, onViewChange]);

//   const handleToggleChange = (payload) => {
//     // FormFieldSet might send boolean, or event, or "grid"/"list". Normalize defensively.
//     if (typeof payload === "boolean") {
//       setToggleInput(payload);
//       return;
//     }

//     if (payload == null) {
//       // null/undefined — toggle boolean
//       setToggleInput((s) => !s);
//       return;
//     }

//     if (typeof payload === "string") {
//       // maybe it sends "grid" or "list"
//       setToggleInput(payload === "grid");
//       return;
//     }

//     // if it's an event-like object with target.checked
//     if (
//       payload &&
//       payload.target &&
//       typeof payload.target.checked === "boolean"
//     ) {
//       setToggleInput(Boolean(payload.target.checked));
//       return;
//     }

//     // fallback: toggle
//     setToggleInput((s) => !s);
//   };

//   useEffect(() => {
//     if (tableId) {
//       const visibleColumns = columns
//         .filter((col) => !col.hide)
//         .map((col) => ({
//           key: col.key,
//           label: col.label,
//         }));
//       // Check if default config already exists
//       const existingConfigStr = localStorage.getItem(
//         `tableConfig_${tableId}_${loginUserId}`,
//       );

//       if (existingConfigStr) {
//         // If config exists, parse it
//         try {
//           let existingConfig = JSON.parse(existingConfigStr);
//           let existingConfigs = [];
//           // Convert to array format if it's a single object
//           if (!Array.isArray(existingConfig)) {
//             existingConfigs = [existingConfig];
//           } else {
//             existingConfigs = existingConfig;
//           }
//           // Find default config or first config
//           const defaultConfig =
//             existingConfigs.find((config) => config.isDefault) ||
//             existingConfigs[0];
//           // Check if columns have changed by comparing arrays
//           const columnsChanged = !areColumnsEqual(
//             defaultConfig.columns,
//             visibleColumns,
//           );

//           if (columnsChanged) {
//             // Update only the columns and add updatedOn timestamp for the default config
//             const updatedConfigs = existingConfigs.map((config) => {
//               if (config.isDefault) {
//                 return {
//                   ...config,
//                   columns: visibleColumns,
//                   updatedOn: new Date().toISOString(),
//                 };
//               }
//               return config;
//             });

//             localStorage.setItem(
//               `tableConfig_${tableId}_${loginUserId}`,
//               JSON.stringify(updatedConfigs),
//             );
//           }
//         } catch (error) {
//           console.error("Error parsing existing table config:", error);
//         }
//       } else {
//         // Create new configuration object if none exists
//         const tableConfig = [
//           {
//             id: Date.now(), // Generate a unique timestamp ID
//             configName: "defaultConfig",
//             columns: visibleColumns,
//             isActive: true,
//             isDefault: true,
//             createdDate: new Date().toISOString(),
//           },
//         ];

//         localStorage.setItem(
//           `tableConfig_${tableId}_${loginUserId}`,
//           JSON.stringify(tableConfig),
//         );
//       }
//     }
//   }, [tableId, columns, loginUserId]);
//   // Helper function to compare column arrays
//   const areColumnsEqual = (cols1, cols2) => {
//     if (!cols1 || !cols2 || cols1.length !== cols2.length) return false;
//     // Sort both arrays by key to ensure consistent comparison
//     const sortedCols1 = [...cols1].sort((a, b) => a.key.localeCompare(b.key));
//     const sortedCols2 = [...cols2].sort((a, b) => a.key.localeCompare(b.key));

//     return sortedCols1.every(
//       (col, index) =>
//         col.key === sortedCols2[index].key &&
//         col.label === sortedCols2[index].label,
//     );
//   };
//   ///////////////Store data in local store on load the component

//   ///////////////Retrieve data from local store where isActive true on load the component
//   // Add a state to track configuration changes

//   const [configUpdateTrigger, setConfigUpdateTrigger] = useState(0);
//   // Function to refresh the active configuration
//   const refreshActiveConfig = useCallback(() => {
//     setConfigUpdateTrigger((prev) => prev + 1);
//   }, []);
//   // Add event listener for storage changes and custom events
//   useEffect(() => {
//     const handleStorageChange = (e) => {
//       if (tableId && e.key === `tableConfig_${tableId}_${loginUserId}`) {
//         refreshActiveConfig();
//       }
//     };

//     const handleCustomEvent = (e) => {
//       if (
//         tableId &&
//         e.detail.tableId === tableId &&
//         e.detail.userId === loginUserId
//       ) {
//         refreshActiveConfig();
//       }
//     };

//     window.addEventListener("storage", handleStorageChange);
//     window.addEventListener("tableConfigUpdated", handleCustomEvent);

//     return () => {
//       window.removeEventListener("storage", handleStorageChange);
//       window.removeEventListener("tableConfigUpdated", handleCustomEvent);
//     };
//   }, [tableId, loginUserId, refreshActiveConfig]);

//   const activeTableConfig = useMemo(() => {
//     if (tableId) {
//       const storageKey = `tableConfig_${tableId}_${loginUserId}`;
//       const rawData = localStorage.getItem(storageKey);

//       if (!rawData) {
//         return null;
//       }

//       try {
//         const tableConfig = JSON.parse(rawData);
//         // First check if it's a proper array
//         if (Array.isArray(tableConfig)) {
//           const active = tableConfig.find(
//             (config) => config && config.isActive === true,
//           );
//           if (active) return active;
//         }
//         // Handle object with numeric keys (looks like an array but isn't)
//         if (tableConfig && typeof tableConfig === "object") {
//           // Check if it's an object with numeric keys
//           for (const key in tableConfig) {
//             if (tableConfig[key] && tableConfig[key].isActive === true) {
//               return tableConfig[key];
//             }
//           }
//         }
//         // Handle single object format
//         if (tableConfig && tableConfig.isActive) {
//           return tableConfig;
//         }

//         return null;
//       } catch (error) {
//         console.error("Error parsing tableConfig:", error);
//         return null;
//       }
//     }
//     return null;
//   }, [tableId, loginUserId, configUpdateTrigger]); // Add configUpdateTrigger to dependencies
//   ///////////////Retrieve data from local store where isActive true on load the component
//   useEffect(() => {
//     // CLEAN FILTER CONDITIONS BEFORE SAVING
//     const cleanAll = (activeTableConfig?.allFilterConditions || []).filter(
//       (c) => c.filterColunm && c.operator,
//     );

//     const cleanAny = (activeTableConfig?.anyFilterConditions || []).filter(
//       (c) => c.filterColunm && c.operator,
//     );
//     if (serverFiltering && onFilterChange && activeTableConfig) {
//       const filterConfig = {
//         allFilterConditions: cleanAll || [],
//         anyFilterConditions: cleanAny || [],
//         columns: activeTableConfig.columns || [],
//         configName: activeTableConfig.configName,
//         configId: activeTableConfig.id,
//       };

//       onFilterChange(filterConfig);
//     }
//   }, [serverFiltering, onFilterChange, activeTableConfig, configUpdateTrigger]);

//   /////////////////////////////////////////Drawer Setup
//   const [isDrawerOpen, setIsDrawerOpen] = useState(false);
//   ///////////////////////////////download button modal setup
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [downloadType, setDownloadType] = useState("");

//   ///////////////
//   const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
//   ///////////////

//   useEffect(() => {
//     if (tableId) {
//       const loadUserConfigs = () => {
//         const existingConfigStr = localStorage.getItem(
//           `tableConfig_${tableId}_${loginUserId}`,
//         );

//         if (existingConfigStr) {
//           try {
//             const parsedConfig = JSON.parse(existingConfigStr);
//             const configs = Array.isArray(parsedConfig)
//               ? parsedConfig
//               : [parsedConfig];

//             // Filter out system config
//             const userOnlyConfigs = configs.filter(
//               (config) => !config.isSystem,
//             );
//             setUserConfigs(userOnlyConfigs);

//             // Find active config
//             const activeConfig = configs.find((config) => config.isActive);
//             if (activeConfig) {
//               setActiveConfigName(
//                 activeConfig.isSystem
//                   ? "System Generated"
//                   : keyToTitle(activeConfig.configName),
//               );
//             } else {
//               setActiveConfigName("System Generated");
//             }
//           } catch (error) {
//             console.error("Error parsing table config:", error);
//             setUserConfigs([]);
//             setActiveConfigName("System Generated");
//           }
//         } else {
//           setUserConfigs([]);
//           setActiveConfigName("System Generated");
//         }
//       };

//       loadUserConfigs();

//       // Listen for config updates
//       const handleConfigUpdate = (e) => {
//         if (
//           tableId &&
//           e.detail.tableId === tableId &&
//           e.detail.userId === loginUserId
//         ) {
//           loadUserConfigs();
//         }
//       };

//       window.addEventListener("tableConfigUpdated", handleConfigUpdate);

//       return () => {
//         window.removeEventListener("tableConfigUpdated", handleConfigUpdate);
//       };
//     }
//   }, [tableId, loginUserId, configUpdateTrigger]);

//   const applyFilterConditions = useCallback(
//     (rowsToFilter) => {
//       if (
//         !tableId ||
//         !activeTableConfig ||
//         !activeTableConfig.allFilterConditions ||
//         activeTableConfig.allFilterConditions.length === 0 ||
//         serverFiltering
//       ) {
//         return rowsToFilter;
//       }

//       const filterConditions = activeTableConfig.allFilterConditions;
//       // Group filters by column
//       const filtersByColumn = filterConditions.reduce((acc, condition) => {
//         const colKey = condition.filterColunm;
//         if (!acc[colKey]) acc[colKey] = [];
//         acc[colKey].push(condition);
//         return acc;
//       }, {});
//       // ---------------- Helpers ----------------
//       const flattenObjectValue = (value) => {
//         if (!value || typeof value !== "object") return String(value ?? "");
//         const extractValues = (obj) =>
//           Object.values(obj)
//             .map((v) =>
//               typeof v === "object" && v !== null
//                 ? extractValues(v)
//                 : String(v ?? ""),
//             )
//             .filter(Boolean);
//         return extractValues(value).join(" ");
//       };

//       const getCellValue = (row, column) => {
//         if (column.valueGetter) return column.valueGetter(row);
//         const rawValue = row[column.key];

//         if (typeof rawValue === "object") {
//           return flattenObjectValue(rawValue);
//         }

//         return rawValue;
//       };

//       return rowsToFilter.filter((row) => {
//         return Object.entries(filtersByColumn).every(
//           ([columnKey, conditions]) => {
//             return conditions.some((condition) => {
//               if (!condition.filterColunm || !condition.operator) return true;

//               const { operator, filterValue, dataType = "string" } = condition;

//               const column = columns.find(
//                 (col) => (col.labelKey || col.key) === columnKey,
//               );
//               if (!column) return true;

//               const cellValue = getCellValue(row, column);

//               if (
//                 cellValue === undefined ||
//                 cellValue === null ||
//                 cellValue === ""
//               ) {
//                 return operator === "isEmpty" || operator === "notEqual";
//               }

//               try {
//                 return compareValues(
//                   cellValue,
//                   filterValue,
//                   operator,
//                   dataType,
//                 );
//               } catch (error) {
//                 console.error("Filter comparison error:", error);
//                 return true;
//               }
//             });
//           },
//         );
//       });
//     },
//     [tableId, activeTableConfig, columns, serverFiltering],
//   );

//   const applyOrFilterConditions = useCallback(
//     (rowsToFilter) => {
//       if (
//         serverFiltering ||
//         !tableId ||
//         !activeTableConfig ||
//         !activeTableConfig.anyFilterConditions ||
//         activeTableConfig.anyFilterConditions.length === 0
//       ) {
//         return rowsToFilter;
//       }
//       const filterConditions = activeTableConfig.anyFilterConditions;
//       const filtersByColumn = filterConditions.reduce((acc, condition) => {
//         const colKey = condition.filterColunm;
//         if (!acc[colKey]) acc[colKey] = [];
//         acc[colKey].push(condition);
//         return acc;
//       }, {});
//       // ---------------- Helpers ----------------
//       const flattenObjectValue = (value) => {
//         if (!value || typeof value !== "object") return String(value ?? "");
//         const extractValues = (obj) =>
//           Object.values(obj)
//             .map((v) =>
//               typeof v === "object" && v !== null
//                 ? extractValues(v)
//                 : String(v ?? ""),
//             )
//             .filter(Boolean);
//         return extractValues(value).join(" ");
//       };

//       const getCellValue = (row, column) => {
//         if (column.valueGetter) return column.valueGetter(row);
//         const rawValue = row[column.key];

//         if (typeof rawValue === "object") {
//           return flattenObjectValue(rawValue);
//         }

//         return rawValue;
//       };

//       return rowsToFilter.filter((row) => {
//         return Object.entries(filtersByColumn).some(
//           ([columnKey, conditions]) => {
//             return conditions.every((condition) => {
//               if (!condition.filterColunm || !condition.operator) return true;

//               const { operator, filterValue, dataType = "string" } = condition;

//               const column = columns.find(
//                 (col) => (col.labelKey || col.key) === columnKey,
//               );
//               if (!column) return true;

//               const cellValue = getCellValue(row, column);

//               if (
//                 cellValue === undefined ||
//                 cellValue === null ||
//                 cellValue === ""
//               ) {
//                 return operator === "isEmpty" || operator === "notEqual";
//               }

//               try {
//                 return compareValues(
//                   cellValue,
//                   filterValue,
//                   operator,
//                   dataType,
//                 );
//               } catch (error) {
//                 console.error("Filter comparison error:", error);
//                 return true;
//               }
//             });
//           },
//         );
//       });
//     },
//     [tableId, activeTableConfig, columns, serverFiltering],
//   );

//   const isValidCondition = (condition) => {
//     return (
//       condition &&
//       condition.filterColunm &&
//       condition.filterColunm.trim() !== "" &&
//       condition.operator &&
//       condition.operator.trim() !== ""
//     );
//   };
//   // Apply filters to rows before pagination
//   const filteredRows = useMemo(() => {
//     if (serverFiltering) {
//       return rows;
//     }
//     let filtered = rows;

//     const allConditions = (activeTableConfig?.allFilterConditions || []).filter(
//       isValidCondition,
//     );
//     const anyConditions = (activeTableConfig?.anyFilterConditions || []).filter(
//       isValidCondition,
//     );

//     if (allConditions.length > 0) {
//       filtered = applyFilterConditions(rows);
//     }

//     if (anyConditions.length > 0) {
//       filtered = applyOrFilterConditions(rows);
//     }

//     return filtered;
//   }, [rows, applyFilterConditions, applyOrFilterConditions, serverFiltering]);

//   // Update pagination to use filtered rows
//   useEffect(() => {
//     if (!serverPagination && filteredRows) {
//       setTotalItems(filteredRows.length);
//     }
//   }, [filteredRows, serverPagination]);

//   // Use the table sort utility hook
//   const { sortedRows, handleSort, sortConfig } = useTableSort(
//     filteredRows,
//     columns,
//   );

//   // const searchFilteredRows = useMemo(() => {
//   //   if (!localSearchValue.trim() || serverPagination) {
//   //     return sortedRows;
//   //   }

//   //   const searchTerm = localSearchValue.trim().toLowerCase();

//   //   return sortedRows.filter((row) =>
//   //     Object.entries(row).some(([key, value]) => {
//   //       if (value === null || value === undefined) return false;

//   //       let textValue = "";

//   //       // Map numeric codes to labels dynamically
//   //       if (key.toLowerCase().includes("gender")) {
//   //         const found = Gender.find((g) => g.value === Number(value));
//   //         textValue = found ? found.label : value;
//   //       } else if (key.toLowerCase().includes("status")) {
//   //         const found = EmployeeStatus.find((s) => s.value === Number(value));
//   //         textValue = found ? found.label : value;
//   //       }
//   //       // Handle nested objects (e.g., { fullName: { first: "John" } })
//   //       else if (typeof value === "object") {
//   //         textValue = Object.values(value).join(" ");
//   //       } else {
//   //         textValue = String(value);
//   //       }

//   //       return textValue.toLowerCase().includes(searchTerm);
//   //     }),
//   //   );
//   // }, [sortedRows, localSearchValue, serverPagination]);

// const searchFilteredRows = useMemo(() => {  // mahee was updated 2302026
//   if (!localSearchValue?.trim() || serverPagination) {
//     return sortedRows;
//   }
//   const searchTerm = localSearchValue.trim().toLowerCase();
//   const normalizeValue = (value) => {
//     if (value == null) return "";
//     if (typeof value === "string") return value;
//     if (typeof value === "number" || typeof value === "boolean")
//       return String(value);
//     if (Array.isArray(value)) {
//       return value.map(normalizeValue).join(" ");
//     }
//     if (typeof value === "object") {
//       return Object.values(value)
//         .map(normalizeValue)
//         .join(" ");
//     }
//     return "";
//   };
//   return sortedRows.filter((row) =>
//     Object.entries(row).some(([key, value]) => {
//       if (value == null) return false;
//       let finalValue = value;
//       if (key.toLowerCase().includes("gender")) {
//         const found = Gender.find(
//           (g) => g.value === Number(value)
//         );
//         finalValue = found?.label ?? value;
//       }
//       else if (key.toLowerCase().includes("status")) {
//         const found = EmployeeStatus.find(
//           (s) => s.value === Number(value)
//         );
//         finalValue = found?.label ?? value;
//       }
//       const searchableText = normalizeValue(finalValue).toLowerCase();
//       return searchableText.includes(searchTerm);
//     })
//   );
// }, [sortedRows, localSearchValue, serverPagination]);

//   const displayedRowsWithFilters = useMemo(() => {
//     if (serverPagination) {
//       return searchFilteredRows;
//     } else {
//       const start = (currentPage - 1) * rowsPerPage;
//       const end = start + rowsPerPage;
//       return searchFilteredRows.slice(start, end);
//     }
//   }, [searchFilteredRows, currentPage, rowsPerPage, serverPagination]);

//   const { totals, firstTotalIndex, lastTotalIndex, totalLabelColspan } =
//     useTableTotals(rows, columns, showCheckbox, showSlNo, displayedRows);

//   useEffect(() => {
//     if (!serverPagination && localSearchValue.trim()) {
//       handlePageChange(1);
//     }
//   }, [localSearchValue]);

//   useEffect(() => {
//     if (!enableKeyboardNavigation || viewMode !== "list") return;

//     const handleKeyDown = (e) => {
//       if (!activeCell) return;

//       const visibleColumns = activeTableConfig?.columns
//         ? activeTableConfig.columns
//             .map((configCol) =>
//               columns.find((column) => column.key === configCol.key),
//             )
//             .filter(Boolean)
//         : columns.filter((column) => !column.hide);

//       const totalColumns = visibleColumns.length;
//       const totalRows = displayedRowsWithFilters.length;

//       let newRowIndex = activeCell.rowIndex;
//       let newColIndex = activeCell.colIndex;

//       switch (e.key) {
//         case "ArrowUp":
//           e.preventDefault();
//           newRowIndex = Math.max(0, activeCell.rowIndex - 1);
//           break;
//         case "ArrowDown":
//           e.preventDefault();
//           newRowIndex = Math.min(totalRows - 1, activeCell.rowIndex + 1);
//           break;
//         case "ArrowLeft":
//           e.preventDefault();
//           newColIndex = Math.max(0, activeCell.colIndex - 1);
//           break;
//         case "ArrowRight":
//           e.preventDefault();
//           newColIndex = Math.min(totalColumns - 1, activeCell.colIndex + 1);
//           break;
//         default:
//           return;
//       }

//       setActiveCell({ rowIndex: newRowIndex, colIndex: newColIndex });
//     };

//     window.addEventListener("keydown", handleKeyDown);
//     return () => window.removeEventListener("keydown", handleKeyDown);
//   }, [
//     activeCell,
//     enableKeyboardNavigation,
//     displayedRowsWithFilters,
//     columns,
//     activeTableConfig,
//     viewMode,
//   ]);

//   // Scroll active cell into view
//   useEffect(() => {
//     if (activeCell && activeCellRef.current) {
//       activeCellRef.current.scrollIntoView({
//         behavior: "smooth",
//         block: "nearest",
//         inline: "nearest",
//       });
//     }
//   }, [activeCell]);

//   useEffect(() => {
//     if (!enableKeyboardNavigation) return;

//     const handleClickOutside = (e) => {
//       const isModalClick = e.target.closest('[role="dialog"]');

//       if (isModalClick) return;

//       if (tableRef.current && !tableRef.current.contains(e.target)) {
//         setActiveCell(null);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [enableKeyboardNavigation]);

//   const activateConfig = (configId) => {
//     const existingConfigStr = localStorage.getItem(
//       `tableConfig_${tableId}_${loginUserId}`,
//     );

//     if (existingConfigStr) {
//       try {
//         const parsedConfig = JSON.parse(existingConfigStr);
//         const configs = Array.isArray(parsedConfig)
//           ? parsedConfig
//           : [parsedConfig];

//         const updatedConfigs = configs.map((config) => ({
//           ...config,
//           isActive: config.id === configId,
//         }));

//         localStorage.setItem(
//           `tableConfig_${tableId}_${loginUserId}`,
//           JSON.stringify(updatedConfigs),
//         );

//         const event = new CustomEvent("tableConfigUpdated", {
//           detail: { tableId, userId: loginUserId },
//         });
//         window.dispatchEvent(event);

//         if (serverFiltering && onFilterChange) {
//           const activeConfig = updatedConfigs.find((c) => c.isActive);
//           const cleanAll = (activeConfig?.allFilterConditions || []).filter(
//             (c) => c.filterColunm && c.operator,
//           );

//           const cleanAny = (activeConfig?.anyFilterConditions || []).filter(
//             (c) => c.filterColunm && c.operator,
//           );
//           if (activeConfig) {
//             onFilterChange({
//               allFilterConditions: cleanAll || [],
//               anyFilterConditions: cleanAny || [],
//               columns: activeConfig.columns || [],
//               configName: activeConfig.configName,
//               configId: activeConfig.id,
//             });
//           }
//         }

//         setConfigUpdateTrigger((prev) => prev + 1);
//       } catch (error) {
//         console.error("Error activating config:", error);
//       }
//     }
//   };

//   const openDrawerWithConfig = (configId) => {
//     activateConfig(configId);
//     // setIsDrawerOpen(true);
//   };

//   const toolbarItems = {
//     search: toolbarRight?.search && (
//       <div key="search" className="md:max-w-40 xl:max-w-max">
//         <CustomSearch
//           size="sm"
//           value={serverPagination ? serverSearchValue : localSearchValue}
//           onChange={
//             serverPagination
//               ? (key, val) => serverOnSearch(key, val)
//               : (key, val) => setLocalSearchValue(val)
//           }
//           keyname="search"
//           rounded="rounded"
//           closeBtn
//         />
//       </div>
//     ),
//     filter: toolbarRight?.filter && (
//       <div key="filter">
//         {userConfigs.length > 0 ? (
//           <div className="relative">
//             <DropdownMenu
//               MenuSection={
//                 <div className="flex flex-col p-1">
//                   {userConfigs.map((config) => (
//                     <Button
//                       key={config.id}
//                       title={keyToTitle(config.configName)}
//                       variant="text"
//                       color={config.isActive ? "primary" : "black"}
//                       className="!justify-start py-1"
//                       onClick={() => openDrawerWithConfig(config.id)}
//                     />
//                   ))}
//                   <Divider className="!my-1" />
//                   <Button
//                     title="Filter Settings"
//                     icon={"settings"}
//                     variant="text"
//                     color="black"
//                     className="!justify-start py-1"
//                     onClick={() => {
//                       setIsDrawerOpen(true);
//                     }}
//                   />
//                 </div>
//               }
//             >
//               <SmartTooltip description={`Active: ${activeConfigName}`} noicon>
//                 <Button
//                   icon="filter_alt"
//                   type="icon"
//                   variant="text"
//                   color="black"
//                 />
//               </SmartTooltip>
//             </DropdownMenu>
//           </div>
//         ) : (
//           <Button
//             type="icon"
//             icon="filter_alt"
//             variant="text"
//             color="black"
//             onClick={() => setIsDrawerOpen(true)}
//           />
//         )}
//       </div>
//     ),
//     download: toolbarRight?.download && (
//       <div key="download">
//         <DropdownMenu
//           MenuSection={
//             <div className="flex flex-col">
//               <Button
//                 title="CSV"
//                 variant="text"
//                 color="black"
//                 className="!justify-start py-1"
//                 onClick={() => {
//                   setDownloadType("CSV");
//                   setIsModalOpen(true);
//                 }}
//               />
//               <Button
//                 title="Excel"
//                 variant="text"
//                 color="black"
//                 className="!justify-start py-1"
//                 onClick={() => {
//                   setDownloadType("EXCEL");
//                   setIsModalOpen(true);
//                 }}
//               />
//             </div>
//           }
//         >
//           <Button icon="download" type="icon" variant="text" color="black" />
//         </DropdownMenu>
//       </div>
//     ),
//     viewToggle: toolbarRight?.viewToggle && (
//       <div key="viewToggle">
//         <FormFieldSet
//           value={toggleInput}
//           noTitle
//           size="sm"
//           rounded="sm"
//           type="toggleInput"
//           onChange={handleToggleChange}
//           leftIcon="dashboard_2"
//           rightIcon="event_list"
//         />
//       </div>
//     ),
//     fullScreen: toolbarRight?.fullScreen && (
//       <div key="fullScreen">
//         <Button
//           icon="fullscreen"
//           type="icon"
//           variant="text"
//           color="black"
//           onClick={() => setIsFullscreenOpen(!isFullscreenOpen)}
//         />
//       </div>
//     ),
//   };
//   const defaultOrder = [
//     "search",
//     "filter",
//     "download",
//     "viewToggle",
//     "add",
//     "fullScreen",
//   ];
//   const order = toolbarRight?.order || defaultOrder;

//   const getPaginationRange = (currentPage, totalPages) => {
//     // Show all pages if few
//     if (totalPages <= 4) {
//       return Array.from({ length: totalPages }, (_, i) => i + 1);
//     }

//     // Page 1
//     if (currentPage === 1) {
//       return [1, 2, "...", totalPages];
//     }

//     // Page 2
//     if (currentPage === 2) {
//       return [1, 2, 3, "...", totalPages];
//     }

//     // Page 3
//     if (currentPage === 3) {
//       return [1, 2, 3, 4, "...", totalPages];
//     }

//     // Last page
//     if (currentPage === totalPages) {
//       return [1, "...", totalPages - 1, totalPages];
//     }

//     // Second last page
//     if (currentPage === totalPages - 1) {
//       return [1, "...", totalPages - 2, totalPages - 1, totalPages];
//     }
//     // Third last page
//     if (currentPage === totalPages - 2) {
//       return [
//         1,
//         "...",
//         totalPages - 3,
//         totalPages - 2,
//         totalPages - 1,
//         totalPages,
//       ];
//     }

//     // Middle pages
//     return [
//       1,
//       "...",
//       currentPage - 1,
//       currentPage,
//       currentPage + 1,
//       "...",
//       totalPages,
//     ];
//   };

//   const PageNumbers = () => {
//     const pages = getPaginationRange(
//       currentPage,
//       serverPagination || serverFiltering
//         ? totalPages
//         : Math.ceil(searchFilteredRows.length / rowsPerPage),
//     );

//     return (
//       <div className="flex items-center">
//         {pages.map((page, index) => {
//           if (page === "...") {
//             return (
//               <span key={`ellipsis-${index}`} className="px-0! text-gray-400">
//                 ...
//               </span>
//             );
//           }

//           return (
//             <Button
//               key={page}
//               size="sm"
//               variant={currentPage === page ? "contained" : "text"}
//               color={currentPage === page ? "primary" : "black"}
//               title={String(page)}
//               className="min-w-[20px] !px-0 !py-0"
//               onClick={() => {
//                 handlePageChange(page);
//                 setLocalSearchValue("");
//               }}
//               data-dropdown-persist="true"
//             />
//           );
//         })}
//       </div>
//     );
//   };

//   const tableBody = (
//     <div className="flex-1 w-full flex flex-col gap-0 overflow-hidden">
//       {!hideToolBar && (
//         <div className="tableTools py-2 px-2 flex justify-between items-center text-sm flex-wrap">
//           <div className="flex justify-between items-center gap-2">
//             <div className="flex items-center gap-2">
//               <span>
//                 {!totalCount && !searchFilteredRows?.length
//                   ? startIndex
//                   : startIndex + 1}
//                 -
//                 {Math.min(
//                   endIndex,
//                   serverPagination || serverFiltering
//                     ? totalCount
//                     : searchFilteredRows.length,
//                 )}
//               </span>
//               <span className="text-nowrap">
//                 {" "}
//                 of{" "}
//                 {serverPagination || serverFiltering
//                   ? totalCount
//                   : searchFilteredRows.length}
//               </span>

//               <div className="flex items-center gap-1">
//                 <Button
//                   size="sm"
//                   type="icon"
//                   variant="text"
//                   color="black"
//                   icon="chevron_left"
//                   onClick={() => {
//                     handlePageChange(currentPage - 1);
//                     setLocalSearchValue("");
//                   }}
//                   disabled={currentPage === 1}
//                   data-dropdown-persist="true"
//                 />
//                 {showPages && <PageNumbers />}

//                 <Button
//                   size="sm"
//                   icon="chevron_right"
//                   type="icon"
//                   variant="text"
//                   color="black"
//                   onClick={() => {
//                     handlePageChange(currentPage + 1);
//                     setLocalSearchValue("");
//                   }}
//                   disabled={
//                     serverPagination || serverFiltering
//                       ? currentPage >= totalPages
//                       : currentPage >=
//                         Math.ceil(searchFilteredRows.length / rowsPerPage)
//                   }
//                   data-dropdown-persist="true"
//                 />
//               </div>
//               <div className="w-32 lg:w-40">
//                 <CustomSelect
//                   size="sm"
//                   placeholder="Select rows"
//                   options={selectOpionGenerator(selectList, rowsPerPage)}
//                   selected={String(rowsPerPage)}
//                   onChange={handleRowsPerPageChange}
//                   data-dropdown-persist="true"
//                 />
//               </div>
//               {(selectedRows.length > 0 ||
//                 (totalSelectedCount !== null && totalSelectedCount > 0)) && (
//                 <div className="flex gap-2 items-center text-xs">
//                   Total Selected rows:{" "}
//                   {totalSelectedCount !== null
//                     ? totalSelectedCount
//                     : selectedRows.length}{" "}
//                   <Button
//                     type="icon"
//                     icon={"close"}
//                     size="xs"
//                     variant="text"
//                     onClick={() => {
//                       setSelectedRows([]);
//                       if (onSelectionChange) {
//                         onSelectionChange([]);
//                       }
//                     }}
//                   />
//                 </div>
//               )}
//               <div className="flex items-center gap-1">
//                 {toolbarLeft?.add &&
//                   toolbarLeft.add.map((item, key) => (
//                     <div key={`toolbar-add-${key}`}>
//                       {item.component ? (
//                         item.component
//                       ) : (
//                         <Button
//                           icon={item?.icon}
//                           type="icon"
//                           variant="text"
//                           color="black"
//                           onClick={item?.onClick}
//                         />
//                       )}
//                     </div>
//                   ))}
//               </div>
//             </div>
//           </div>

//           <div className="toolbar-right flex items-center gap-2 mr-1">
//             {order.map((key) => {
//               if (key.startsWith("add[")) {
//                 const index = parseInt(key.match(/\[(\d+)\]/)?.[1]);
//                 const addWrapper = toolbarRight?.add?.[0];
//                 if (!addWrapper) return null;

//                 const Comp = addWrapper.component;

//                 if (React.isValidElement(Comp) && Comp.props?.children) {
//                   const childrenArray = React.Children.toArray(
//                     Comp.props.children,
//                   );
//                   const targetChild = childrenArray[index];
//                   if (!targetChild) return null;

//                   return (
//                     <div key={key} className="flex items-center">
//                       {targetChild}
//                     </div>
//                   );
//                 }

//                 if (React.isValidElement(Comp) && index === 0) {
//                   return (
//                     <div key={key} className="flex items-center">
//                       {Comp}
//                     </div>
//                   );
//                 }

//                 return null;
//               }

//               if (key === "add") {
//                 const addWrapper = toolbarRight?.add?.[0];
//                 if (!addWrapper) return null;

//                 const Comp = addWrapper.component;
//                 if (React.isValidElement(Comp)) {
//                   if (Comp.props?.children) {
//                     return (
//                       <div
//                         key="add"
//                         className="flex flex-row items-center gap-2"
//                       >
//                         {React.Children.toArray(Comp.props.children)}
//                       </div>
//                     );
//                   }

//                   return (
//                     <div key="add" className="flex flex-row items-center gap-2">
//                       {Comp}
//                     </div>
//                   );
//                 }

//                 return null;
//               }

//               if (toolbarItems[key]) {
//                 return <div key={key}>{toolbarItems[key]}</div>;
//               }

//               return null;
//             })}
//           </div>
//         </div>
//       )}

//       {viewMode === "list" && (
//         <React.Fragment>
//           <div
//             className={`tableWrapper flex-1  flex items-start  ${displayedRowsWithFilters.length === 0 ? "overflow-hidden" : "overflow-auto"}`}
//           >
//             <table className="min-w-full w-fit table-auto" ref={tableRef}>
//               <thead className={`${style.stickyHeader}`}>
//                 <tr className={`${style.headerRow} ${style.row}`}>
//                   {showCheckbox && (
//                     <th
//                       className={`${style.headerCell} ${style.cell} w-12 min-w-12 ${
//                         isColumnFixed(-2, fixedCol) || fixedCol > 0
//                           ? `${style.stickyCell} ${headerFixedCellBg}`
//                           : ""
//                       }`}
//                       style={
//                         isColumnFixed(-2, fixedCol) || fixedCol > 0
//                           ? {
//                               ...fixedColumnStyles,
//                               left: calculateLeftValue(
//                                 columnWidths,
//                                 -2,
//                                 checkboxWidth,
//                                 slNoWidth,
//                                 showCheckbox,
//                                 showSlNo,
//                               ),
//                               zIndex: 40,
//                             }
//                           : {}
//                       }
//                     >
//                       {!singleSelection && !disableSelection && (
//                         <input
//                           type="checkbox"
//                           className="w-3 h-3"
//                           checked={
//                             displayedRowsWithFilters.length > 0 &&
//                             displayedRowsWithFilters.every((row) =>
//                               selectedRows.includes(
//                                 row.id || displayedRowsWithFilters.indexOf(row),
//                               ),
//                             )
//                           }
//                           onChange={() => {
//                             const visibleRowIds = displayedRowsWithFilters.map(
//                               (row, idx) => row.id || idx,
//                             );
//                             const allVisibleSelected = visibleRowIds.every(
//                               (id) => selectedRows.includes(id),
//                             );

//                             if (allVisibleSelected) {
//                               setSelectedRows(
//                                 selectedRows.filter(
//                                   (id) => !visibleRowIds.includes(id),
//                                 ),
//                               );
//                             } else {
//                               setSelectedRows([
//                                 ...selectedRows.filter(
//                                   (id) => !visibleRowIds.includes(id),
//                                 ),
//                                 ...visibleRowIds,
//                               ]);
//                             }

//                             if (onSelectionChange) {
//                               onSelectionChange(
//                                 allVisibleSelected
//                                   ? selectedRows.filter(
//                                       (id) => !visibleRowIds.includes(id),
//                                     )
//                                   : [
//                                       ...selectedRows.filter(
//                                         (id) => !visibleRowIds.includes(id),
//                                       ),
//                                       ...visibleRowIds,
//                                     ],
//                               );
//                             }
//                           }}
//                         />
//                       )}
//                     </th>
//                   )}
//                   {showSlNo && (
//                     <th
//                       className={`${style.headerCell} ${style.cell} w-16 min-w-[64px] ${
//                         isColumnFixed(-1, fixedCol) || fixedCol > 0
//                           ? `${style.stickyCell} ${headerFixedCellBg}`
//                           : ""
//                       }`}
//                       style={
//                         isColumnFixed(-1, fixedCol) || fixedCol > 0
//                           ? {
//                               ...fixedColumnStyles,
//                               left: calculateLeftValue(
//                                 columnWidths,
//                                 -1,
//                                 checkboxWidth,
//                                 slNoWidth,
//                                 showCheckbox,
//                                 showSlNo,
//                               ),
//                               zIndex: 40,
//                             }
//                           : {}
//                       }
//                     >
//                       S.No
//                     </th>
//                   )}
//                   {(activeTableConfig?.columns
//                     ? activeTableConfig.columns
//                         .map((configCol) =>
//                           columns.find(
//                             (column) => column.key === configCol.key,
//                           ),
//                         )
//                         .filter(Boolean)
//                     : columns.filter((column) => !column.hide)
//                   ).map((column, colIndex) => {
//                     const { classes, styles: columnStyles } =
//                       processColumnWidth(column.width);
//                     const isFixed = isColumnFixed(colIndex, fixedCol);
//                     const leftPosition = calculateLeftValue(
//                       columnWidths,
//                       colIndex,
//                       checkboxWidth,
//                       slNoWidth,
//                       showCheckbox,
//                       showSlNo,
//                     );

//                     return (
//                       <th
//                         key={colIndex}
//                         className={`${style.headerCell} ${style.cell} ${classes} ${
//                           isFixed
//                             ? `${style.stickyCell} ${headerFixedCellBg}`
//                             : ""
//                         }`}
//                         style={{
//                           ...columnStyles,
//                           ...(isFixed
//                             ? {
//                                 ...fixedColumnStyles,
//                                 left: `${leftPosition}px`,
//                                 zIndex: 30,
//                               }
//                             : {}),
//                         }}
//                       >
//                         <div className="flex justify-between items-center gap-5">
//                           <div className="flex items-center gap-2">
//                             {column.label}
//                             {column.sortfun &&
//                               sortConfig.key === column.key && (
//                                 <span className="material-symbols-outlined !text-xs text-primary-200">
//                                   {sortConfig.direction === "asc"
//                                     ? "north"
//                                     : "south"}
//                                 </span>
//                               )}
//                           </div>
//                           {column.key !== "" && column.columnMenu !== false && (
//                             <div>
//                               <DropdownMenuNew
//                                 MenuSection={
//                                   <div className="p-1 flex flex-col gap-1">
//                                     {column.dataType !== "array" && (
//                                       <>
//                                         <Button
//                                           color={
//                                             sortConfig.key === column.key &&
//                                             sortConfig.direction === "asc"
//                                               ? "primary"
//                                               : "black"
//                                           }
//                                           title={"Sort by ASC"}
//                                           variant="text"
//                                           size="sm"
//                                           icon="north"
//                                           className="w-full text-left justify-start !px-1"
//                                           onClick={() => {
//                                             if (column.sortfun) {
//                                               column.sortfun("asc");
//                                             } else {
//                                               handleSort(column.key, "asc");
//                                             }
//                                           }}
//                                         />
//                                         <Button
//                                           color={
//                                             sortConfig.key === column.key &&
//                                             sortConfig.direction === "desc"
//                                               ? "primary"
//                                               : "black"
//                                           }
//                                           title={"Sort by DESC"}
//                                           variant="text"
//                                           size="sm"
//                                           icon="south"
//                                           className="w-full text-left justify-start !px-1"
//                                           onClick={() => {
//                                             if (column.sortfun) {
//                                               column.sortfun("desc");
//                                             } else {
//                                               handleSort(column.key, "desc");
//                                             }
//                                           }}
//                                         />
//                                         <Divider className="!my-1" />
//                                       </>
//                                     )}
//                                     <Button
//                                       disabled
//                                       color="black"
//                                       title={"Hide Column"}
//                                       variant="text"
//                                       size="sm"
//                                       icon="visibility_off"
//                                       className="w-full text-left justify-start !px-1"
//                                     />
//                                   </div>
//                                 }
//                               >
//                                 <Button
//                                   color="black"
//                                   size="xs"
//                                   title={"More"}
//                                   icon="more_vert"
//                                   type="icon"
//                                   variant="text"
//                                 />
//                               </DropdownMenuNew>
//                             </div>
//                           )}
//                         </div>
//                       </th>
//                     );
//                   })}
//                 </tr>
//               </thead>
//               <tbody>
//                 {displayedRowsWithFilters.length === 0 ? (
//                   <tr>
//                     <td
//                       className="text-center"
//                       colSpan={
//                         (activeTableConfig?.columns
//                           ? activeTableConfig.columns.filter((configCol) =>
//                               columns.find(
//                                 (col) => col.key === configCol.key && !col.hide,
//                               ),
//                             ).length
//                           : columns.filter((col) => !col.hide).length) +
//                         (showSlNo ? 1 : 0) +
//                         (showCheckbox ? 1 : 0)
//                       }
//                     >
//                       {/* <div className="flex justify-center py-8">
//                         <NoData />
//                       </div> */}
//                     </td>
//                   </tr>
//                 ) : (
//                   displayedRowsWithFilters.map((row, rowIndex) => {
//                     const rowId = row.id || rowIndex;
//                     const isExpanded = expandedRows[rowId];
//                     const rowClass = processRowClass(row, columns);

//                     return (
//                       <React.Fragment key={rowId}>
//                         <tr
//                           className={`${style.bodyRow} ${style.row} ${rowClass} group`}
//                         >
//                           {showCheckbox && (
//                             <td
//                               className={`${style.bodyCell} ${style.cell} w-12 min-w-[48px] ${
//                                 isColumnFixed(-2, fixedCol) || fixedCol > 0
//                                   ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
//                                   : ""
//                               }`}
//                               style={
//                                 isColumnFixed(-2, fixedCol) || fixedCol > 0
//                                   ? {
//                                       ...fixedColumnStyles,
//                                       left: calculateLeftValue(
//                                         columnWidths,
//                                         -2,
//                                         checkboxWidth,
//                                         slNoWidth,
//                                         showCheckbox,
//                                         showSlNo,
//                                       ),
//                                       zIndex: 20,
//                                     }
//                                   : {}
//                               }
//                             >
//                               <input
//                                 type="checkbox"
//                                 className="w-3 h-3"
//                                 checked={selectedRows.includes(
//                                   row.id || rowIndex,
//                                 )}
//                                 disabled={disableSelection}
//                                 onChange={(e) => {
//                                   const rowId = row.id || rowIndex;
//                                   if (singleSelection) {
//                                     if (e.target.checked) {
//                                       setSelectedRows([rowId]);
//                                       if (onSelectionChange)
//                                         onSelectionChange([rowId]);
//                                     } else {
//                                       setSelectedRows([]);
//                                       if (onSelectionChange)
//                                         onSelectionChange([]);
//                                     }
//                                   } else {
//                                     if (e.target.checked) {
//                                       const newSelected = [
//                                         ...selectedRows,
//                                         rowId,
//                                       ];
//                                       setSelectedRows(newSelected);
//                                       if (onSelectionChange)
//                                         onSelectionChange(newSelected);
//                                     } else {
//                                       const newSelected = selectedRows.filter(
//                                         (id) => id !== rowId,
//                                       );
//                                       setSelectedRows(newSelected);
//                                       if (onSelectionChange)
//                                         onSelectionChange(newSelected);
//                                     }
//                                   }
//                                 }}
//                               />
//                             </td>
//                           )}
//                           {showSlNo && (
//                             <td
//                               className={`${style.bodyCell} ${style.cell} w-16 min-w-[64px] ${
//                                 isColumnFixed(-1, fixedCol) || fixedCol > 0
//                                   ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
//                                   : ""
//                               }`}
//                               style={
//                                 isColumnFixed(-1, fixedCol) || fixedCol > 0
//                                   ? {
//                                       ...fixedColumnStyles,
//                                       left: calculateLeftValue(
//                                         columnWidths,
//                                         -1,
//                                         checkboxWidth,
//                                         slNoWidth,
//                                         showCheckbox,
//                                         showSlNo,
//                                       ),
//                                       zIndex: 20,
//                                     }
//                                   : {}
//                               }
//                             >
//                               {startIndex + rowIndex + 1}
//                             </td>
//                           )}
//                           {(activeTableConfig?.columns
//                             ? activeTableConfig.columns
//                                 .map((configCol) =>
//                                   columns.find(
//                                     (column) => column.key === configCol.key,
//                                   ),
//                                 )
//                                 .filter(Boolean)
//                             : columns.filter((column) => !column.hide)
//                           ).map((column, colIndex) => {
//                             const { classes, styles: columnStyles } =
//                               processColumnWidth(column.width);
//                             const cellClass = processCellClass(row, column);
//                             const isFixed = isColumnFixed(colIndex, fixedCol);
//                             const leftPosition = calculateLeftValue(
//                               columnWidths,
//                               colIndex,
//                               checkboxWidth,
//                               slNoWidth,
//                               showCheckbox,
//                               showSlNo,
//                             );

//                             const isActiveCell =
//                               enableKeyboardNavigation &&
//                               activeCell?.rowIndex === rowIndex &&
//                               activeCell?.colIndex === colIndex;

//                             return (
//                               <td
//                                 key={colIndex}
//                                 ref={isActiveCell ? activeCellRef : null}
//                                 className={`${style.bodyCell} ${style.cell} ${classes} ${cellClass} ${
//                                   isFixed
//                                     ? `${style.stickyCell} ${fixedCellBg} ${rowClass}`
//                                     : ""
//                                 } ${
//                                   isActiveCell
//                                     ? `border-1 border-${color}-500`
//                                     : ""
//                                 } outline-none`}
//                                 style={{
//                                   ...columnStyles,
//                                   ...(isFixed
//                                     ? {
//                                         ...fixedColumnStyles,
//                                         left: `${leftPosition}px`,
//                                         zIndex: 20,
//                                       }
//                                     : {}),
//                                 }}
//                                 onClick={() => {
//                                   if (enableKeyboardNavigation) {
//                                     setActiveCell({ rowIndex, colIndex });
//                                   }
//                                 }}
//                                 tabIndex={
//                                   enableKeyboardNavigation ? 0 : undefined
//                                 }
//                               >
//                                 {column.render
//                                   ? column.render(row)
//                                   : row[column.key]}
//                               </td>
//                             );
//                           })}
//                         </tr>

//                         {isExpanded && accordion && (
//                           <tr className={style.accordionRow}>
//                             <td
//                               colSpan={
//                                 (activeTableConfig?.columns
//                                   ? activeTableConfig.columns.filter(
//                                       (configCol) =>
//                                         columns.find(
//                                           (col) =>
//                                             col.key === configCol.key &&
//                                             !col.hide,
//                                         ),
//                                     ).length
//                                   : columns.filter((col) => !col.hide).length) +
//                                 (showSlNo ? 1 : 0) +
//                                 (showCheckbox ? 1 : 0)
//                               }
//                               className={style.accordionCell}
//                             >
//                               <div className="w-full">
//                                 {typeof accordion === "function"
//                                   ? accordion(row, isExpanded)
//                                   : React.isValidElement(accordion)
//                                     ? React.cloneElement(accordion, {
//                                         row,
//                                         data: row,
//                                         accordionType: isExpanded,
//                                       })
//                                     : accordion}
//                               </div>
//                             </td>
//                           </tr>
//                         )}
//                       </React.Fragment>
//                     );
//                   })
//                 )}
//               </tbody>
//               {firstTotalIndex >= 0 &&
//                 (activeTableConfig?.columns
//                   ? activeTableConfig.columns
//                       .map((configCol) =>
//                         columns.find((column) => column.key === configCol.key),
//                       )
//                       .filter(Boolean)
//                       .some((column) => column.total)
//                   : columns
//                       .filter((column) => !column.hide)
//                       .some((column) => column.total)) && (
//                   <tfoot style={{ position: "relative", zIndex: 2 }}>
//                     <tr className={`${totalRowStyle}  bg-gray-100`}>
//                       {firstTotalIndex > 0 && (
//                         <td
//                           colSpan={
//                             (activeTableConfig?.columns
//                               ? activeTableConfig.columns
//                                   .map((configCol) =>
//                                     columns.find(
//                                       (column) => column.key === configCol.key,
//                                     ),
//                                   )
//                                   .filter(Boolean)
//                                   .filter(
//                                     (column) =>
//                                       columns.indexOf(column) <
//                                         firstTotalIndex && !column.hide,
//                                   )
//                               : columns.filter(
//                                   (col) =>
//                                     !col.hide &&
//                                     columns.indexOf(col) < firstTotalIndex,
//                                 )
//                             ).length +
//                             (showCheckbox ? 1 : 0) +
//                             (showSlNo ? 1 : 0)
//                           }
//                           className={`${style.footerCell} ${style.cell} text-right font-semibold`}
//                           style={
//                             fixedCol > 0
//                               ? {
//                                   position: "sticky",
//                                   left: 0,
//                                   zIndex: 10,
//                                   backgroundColor: "inherit",
//                                 }
//                               : {}
//                           }
//                         >
//                           {totalText}
//                         </td>
//                       )}
//                       {(activeTableConfig?.columns
//                         ? activeTableConfig.columns
//                             .map((configCol) =>
//                               columns.find(
//                                 (column) => column.key === configCol.key,
//                               ),
//                             )
//                             .filter(Boolean)
//                         : columns.filter((column) => !column.hide)
//                       ).map((column, colIndex) => {
//                         const originalIndex = columns.findIndex(
//                           (col) => col.key === column.key,
//                         );

//                         if (originalIndex < firstTotalIndex) return null;
//                         const dynamicCellClass = column.getCellClass
//                           ? column.getCellClass({})
//                           : "";

//                         const isFooterFixed = colIndex < fixedCol;

//                         return (
//                           <td
//                             key={`footer-${column.key}-${colIndex}`}
//                             className={`px-4 py-2 truncate overflow-hidden whitespace-nowrap text-right ${
//                               originalIndex <= lastTotalIndex
//                                 ? totalCellStyle
//                                 : ""
//                             } ${column.className || ""} ${dynamicCellClass}`}
//                             style={{
//                               ...processColumnWidth(column.width).styles,
//                               ...(isFooterFixed
//                                 ? {
//                                     position: "sticky",
//                                     left: `${calculateLeftValue(
//                                       columnWidths,
//                                       colIndex,
//                                       checkboxWidth,
//                                       slNoWidth,
//                                       showCheckbox,
//                                       showSlNo,
//                                     )}px`,
//                                     zIndex: 10,
//                                     backgroundColor: "inherit",
//                                   }
//                                 : {
//                                     // Non-fixed: no z-index, no position — slides under fixed cols
//                                     position: "relative",
//                                     zIndex: 0,
//                                   }),
//                             }}
//                           >
//                             {column.total
//                               ? column.totalRender
//                                 ? column.totalRender(totals[column.key], totals)
//                                 : formatNumber(totals[column.key]) || "0.00"
//                               : ""}
//                           </td>
//                         );
//                       })}
//                     </tr>
//                   </tfoot>
//                 )}
//             </table>
//           </div>
//           {displayedRowsWithFilters.length === 0 && (
//             <div className="flex justify-center py-8">
//               <NoData noImage={noImage} desc={noDataDescription} />
//             </div>
//           )}
//         </React.Fragment>
//       )}

//       {viewMode === "grid" &&
//         gridViewData &&
//         React.isValidElement(gridViewData) &&
//         React.cloneElement(gridViewData, {})}
//     </div>
//   );

//   return (
//     <div className="flex-1 w-full flex flex-col gap-1 overflow-hidden">
//       {!isFullscreenOpen && tableBody}

//       <DrawerAdv
//         isOpen={isDrawerOpen}
//         onClose={() => setIsDrawerOpen(false)}
//         // position="bottom"
//         title={"Table Filter"}
//         closeOnOutsideClick
//         height="h-[90%]"
//         width="2xl:w-7xl xl:w-5xl lg:w-3xl md:w-xl"
//       >
//         <TableDrawerData
//           columns={columns}
//           rows={rows}
//           userId={loginUserId}
//           tableId={tableId}
//           showSlNo={showSlNo}
//           fixedCol={fixedCol}
//           onFilterUpdate={handleFilterUpdate}
//           serverFiltering={serverFiltering}
//           onServerFilterChange={onFilterChange}
//           onDrawerClose={() => setIsDrawerOpen(false)}
//         />
//       </DrawerAdv>
//       <Modal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         title={`Download ${downloadType} file`}
//         width="max-w-4xl"
//         closeOnOutsideClick
//       >
//         <TableDownloadModalData
//           downloadType={downloadType}
//           columns={columns}
//           rows={rows}
//           selectedRows={selectedRows}
//           currentPage={currentPage}
//           rowsPerPage={rowsPerPage}
//           onClose={() => setIsModalOpen(false)}
//         />
//       </Modal>

//       <Modal
//         //title="Table"
//         showCloseButton={false}
//         isOpen={isFullscreenOpen}
//         onClose={() => setIsFullscreenOpen(false)}
//         width="max-w-[100vw]"
//         wrapperClassName="p-0!"
//         panelClassName="!max-h-[100vh] h-[100dvh] rounded-none!"
//         bodyClassName="p-0! flex flex-1 overflow-hidden"
//         closeOnOutsideClick
//         zIndex={48}
//       >
//         <div className="w-full overflow-hidden flex flex-col flex-1">
//           <div className="flex justify-end p-2">
//             <Button
//               onClick={() => setIsFullscreenOpen(false)}
//               type="icon"
//               icon={"close"}
//               variant="text"
//               size="xs-sm"
//               color="danger"
//             />
//           </div>
//           {tableBody}
//         </div>
//       </Modal>
//     </div>
//   );
// };

// export default TableAdv;
